// ==================== FIXED ADMIN ATTENDANCE COMPONENT ====================
// CRITICAL FIX: Manual check-in/out now sends proper ISO datetime strings
// Previous bug: Was sending just time "10:00" instead of full datetime

import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  UserCheck,
  Users,
  Download,
  Upload,
  Filter,
  Search,
  Plus,
  Edit,
  Trash2,
  Eye,
  Info,
  Settings,
  BarChart3,
  RotateCw,
  X,
  RotateCcw,
  WandSparkles
} from "lucide-react";
// ✅ Import apiFetch for automatic token refresh
import { apiFetch } from "../context/AuthContext";
import { useProfile } from "../hooks/useProfile";
import { ThemeContext } from "./theme";
import { LanguageContext } from "./LanguageContext";
import ReusableDropdown from "../components/ReusableDropdown";
import AlertModal from "../components/AlertModal";
import templateFile from "../Documents/Attendance_template.xlsx";
import LocationScheduleImport from "./LocationScheduleImport";
import DefaultLocationModal from "../components/DefaultLocationModal";

const translations = {
  en: {
    attendanceManagement: "Attendance Management",
    infoText:
      "Overtime and tardiness are calculated based on client settings",
    filtersActions: "Filters & Actions",
    showing: "Showing",
    of: "of",
    records: "records",
    searchEmployee: "Search Employee",
    typeEmployeeName: "Type employee name...",
    statusFilter: "Status Filter",
    allStatuses: "All statuses",
    presentOnly: "Present Only",
    absentOnly: "Absent Only",
    lateOnly: "Late Only",
    selectDate: "Select Date",
    refresh: "Refresh",
    recalculating: "Recalculating...",
    exportCSV: "Export CSV",
    exportTemplate: "Export Template",
    dailyAttendance: "Daily Attendance Records",
    all: "All",
    present: "Present",
    absent: "Absent",
    late: "Late",
    onLeave: "On Leave",
    employeeID: "ID",
    employeeName: "Employee Name",
    date: "Date",
    checkIn: "Check-In",
    checkOut: "Check-Out",
    hoursWorked: "Hours Worked",
    status: "Status",
    overtime: "Overtime",
    tardiness: "Tardiness",
    monthlyOvertimeTotal: "Monthly Overtime Total",
    monthlyTardinessTotal: "Monthly Tardiness Total",
    client: "Client",
    actions: "Actions",
    hours: "hours",
    resetCheckIn: "Reset check-in to",
    resetCheckOut: "Reset check-out to",
    viewHistory: "View history",
    noRecordsFound: "No Attendance Records Found",
    noRecordsMatch: "No records match the selected filters.",
    attendanceHistory: "Attendance History",
    checkInUpdated: "✅ Check-in time updated and recalculated!",
    checkOutUpdated: "✅ Check-out time updated and recalculated!",
    refreshSuccess:
      "✅ Attendance data refreshed and recalculated successfully!",
    refreshFailed: "❌ Failed to refresh attendance data",
    updateCheckInFailed: "❌ Failed to update check-in time",
    updateCheckOutFailed: "❌ Failed to update check-out time",
    noDataExport: "No data to export!",
    clearAll: "Clear All",
    dateTooOldError: "Cannot edit records older than 7 days",
    dateTooOldTooltip: "This record is older than 7 days and cannot be edited",
  },
  ar: {
    attendanceManagement: "نظام إدارة الحضور",
    infoText:
      "يتم حساب الوقت الإضافي والتأخير بناءً على إعدادات العميل المحددة",
    filtersActions: "الفلاتر والإجراءات",
    showing: "عرض",
    of: "من",
    records: "سجل",
    searchEmployee: "البحث عن موظف",
    typeEmployeeName: "اكتب اسم الموظف...",
    statusFilter: "فلتر الحالة",
    allStatuses: "جميع الحالات",
    presentOnly: "الحاضرون فقط",
    absentOnly: "الغائبون فقط",
    lateOnly: "المتأخرون فقط",
    selectDate: "اختر التاريخ",
    refresh: "تحديث",
    recalculating: "جارٍ إعادة الحساب...",
    exportCSV: "تنزيل CSV",
    exportTemplate: "تنزيل القالب",
    dailyAttendance: "سجلات الحضور اليومية",
    all: "الكل",
    present: "حاضر",
    absent: "غائب",
    late: "متأخر",
    onLeave: "في إجازة",
    restDay: "يوم راحة",
    rest_day:"يوم راحة",
    employeeID: "رقم الموظف",
    employeeName: "اسم الموظف",
    date: "التاريخ",
    checkIn: "تسجيل الدخول",
    checkOut: "تسجيل الخروج",
    hoursWorked: "ساعات العمل",
    status: "الحالة",
    overtime: "وقت إضافي",
    tardiness: "تأخير",
    monthlyOvertimeTotal: "إجمالي الوقت الإضافي الشهري",
    monthlyTardinessTotal: "إجمالي التأخير الشهري",
    client: "العميل",
    actions: "الإجراءات",
    hours: "ساعة",
    resetCheckIn: "إعادة تعيين الدخول إلى 10:00",
    resetCheckOut: "إعادة تعيين الخروج إلى 18:00",
    viewHistory: "عرض السجل",
    noRecordsFound: "لم يتم العثور على سجلات حضور",
    noRecordsMatch: "لا توجد سجلات تطابق الفلاتر المحددة.",
    attendanceHistory: "سجل الحضور",
    checkInUpdated: "✅ تم تحديث وقت الدخول وإعادة الحساب!",
    checkOutUpdated: "✅ تم تحديث وقت الخروج وإعادة الحساب!",
    refreshSuccess: "✅ تم تحديث بيانات الحضور وإعادة حسابها بنجاح!",
    refreshFailed: "❌ فشل تحديث بيانات الحضور",
    updateCheckInFailed: "❌ فشل تحديث وقت الدخول",
    updateCheckOutFailed: "❌ فشل تحديث وقت الخروج",
    noDataExport: "لا توجد بيانات للتصدير!",
    clearAll: "مسح الكل",
    dateTooOldError: "لا يمكن تحرير السجلات الأكثر من 7 أيام",
    dateTooOldTooltip: "هذا السجل أقدم من 7 أيام ولا يمكن تعديله",
  },
};

const Attendance = () => {
  const { theme } = useContext(ThemeContext);
  const { language } = useContext(LanguageContext);
  const t = translations[language];
  const BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";
  const isRTL = language === "ar";

  // Profile from /users/me
  const { profile } = useProfile();

  // Removed axios interceptor refs (using centralized apiFetch)

  // State Management
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [employeeHistory, setEmployeeHistory] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [employees, setEmployees] = useState([]); // All employees for name lookup
  const [visibleEmployees, setVisibleEmployees] = useState([]); // Visibility-filtered employees
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("all");
  // local input value for the status dropdown so typing shows suggestions
  const [statusSearch, setStatusSearch] = useState(
    filterStatus === 'all' ? t.allStatuses :
      filterStatus === 'present' ? t.presentOnly :
        filterStatus === 'absent' ? t.absentOnly :
        filterStatus === 'late' ? t.lateOnly :
        filterStatus === 'onLeave' ? t.onLeave : ''
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [isRecalculating, setIsRecalculating] = useState(false);
  const [loading, setLoading] = useState(false);
  const [clientConfig, setClientConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);

  // Permission States
  const [permissions, setPermissions] = useState({ modules: [], features: {} });
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const [userRole, setUserRole] = useState("");

  // CSV Import States
  const [showImportModal, setShowImportModal] = useState(false);
  const [importPreview, setImportPreview] = useState([]);
  const [importResult, setImportResult] = useState(null);
  const [importActiveTab, setImportActiveTab] = useState("preview");
  const [isImporting, setIsImporting] = useState(false);
  const [duplicateSelections, setDuplicateSelections] = useState({});
  const [isResolvingDuplicates, setIsResolvingDuplicates] = useState(false);

  // Toast Notification State
  const [toast, setToast] = useState(null);

  // AlertModal State
  const [alertModal, setAlertModal] = useState({
    open: false,
    title: '',
    message: '',
    variant: 'info', // 'info', 'success', 'error'
    showCancel: false,
    onConfirm: null,
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);

  // Get view type (manager vs admin view for manager accounts) - make it reactive
  const [viewType, setViewType] = useState(localStorage.getItem('managerViewType') || 'manager');
  const isAdminView = viewType === 'admin';
  const isManagerView = viewType === 'manager';

  // Axios interceptors removed — apiFetch handles auth and refresh centrally

  // ✅ Listen for viewType changes
  useEffect(() => {
    const handleStorageChange = () => {
      setViewType(localStorage.getItem('managerViewType') || 'manager');
    };

    // Listen for custom event when viewType changes
    window.addEventListener('managerViewTypeChanged', handleStorageChange);

    return () => {
      window.removeEventListener('managerViewTypeChanged', handleStorageChange);
    };
  }, []);


  // ✅ Load permissions and role info
  useEffect(() => {
    setUserRole(profile?.role || localStorage.getItem("role") || "");
    // Only fetch permissions if in admin view
    if (isAdminView) {
      fetchPermissions();
    }
  }, [isAdminView, profile]);

  // ✅ Load client configuration on mount
  useEffect(() => {
    loadClientConfig();
  }, []);

  // ==================== HELPER: SHOW ALERT MODAL ====================
  const showAlert = (title, message, variant = 'info', onConfirm = null, showCancel = false) => {
    setAlertModal({
      open: true,
      title,
      message,
      variant,
      showCancel,
      onConfirm,
    });
  };

  const closeAlert = () => {
    setAlertModal({ ...alertModal, open: false });
  };

  const handleAlertConfirm = () => {
    if (alertModal.onConfirm) {
      alertModal.onConfirm();
    }
    closeAlert();
  };

  // ==================== HELPER: SHOW TOAST NOTIFICATION ====================
  const showToast = (message, duration = 3000) => {
    setToast(message);
    setTimeout(() => {
      setToast(null);
    }, duration);
  };

  // ==================== HELPER: VALIDATE TOKEN ====================
  const validateToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.error("❌ No authentication token found");
      return false;
    }

    try {
      // Basic token validation - check if it looks like a JWT
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.error("❌ Invalid token format - expected 3 parts, got:", parts.length);
        return false;
      }

      // Try to decode payload to verify it's valid JSON
      try {
        // Convert base64url to base64 (JWT uses base64url encoding)
        let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
        // Add padding if needed
        while (base64.length % 4) {
          base64 += '=';
        }

        const payload = JSON.parse(atob(base64));
        console.log("✅ Token payload decoded:", {
          role: payload.role,
          infinityCode: payload.infinityCode,
          email: payload.employeeEmail,
          hasExp: !!payload.exp,
          exp: payload.exp ? new Date(payload.exp * 1000).toISOString() : 'none',
          isExpired: payload.exp ? (payload.exp * 1000 < Date.now()) : false
        });

        // Token format is valid - don't check expiration on frontend
        // Let backend handle expiration to avoid clock skew issues
        return true;
      } catch (decodeError) {
        console.error("❌ Cannot decode token payload:", decodeError);
        console.error("Token parts[1] length:", parts[1].length);
        console.error("Token parts[1] preview:", parts[1].substring(0, 50));
        return false;
      }
    } catch (error) {
      console.error("❌ Error validating token:", error);
      return false;
    }
  };

  // ==================== LOAD CLIENT CONFIGURATION ====================
  const loadClientConfig = async () => {
    try {
      setConfigLoading(true);

      // Validate token before making request
      if (!validateToken()) {
        console.error("❌ Cannot load config - invalid token");
        setClientConfig({
          workStartTime: "09:00",
          workEndTime: "18:00",
          workingHoursPerDay: 8,
          lateThresholdMinutes: 15,
        });
        return;
      }

      
      const clientName = profile?.clientName || localStorage.getItem("clientName") || "Infinity";
      console.log(`🔧 Loading configuration for client: ${clientName}`);

      // Use the GET /api/company-config/:clientName route
      const response = await apiFetch(
        `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}`,
        {
          method: 'GET',
          headers: { "Accept-Language": language }
        }
      );
      const data = await response.json();

      if (data.success && data.data) {
        const configData = data.data;
        setClientConfig(configData);
        console.log(`✅ Client config loaded:`, configData);
        console.log(`   Work Start: ${configData.workStartTime}`);
        console.log(`   Work End: ${configData.workEndTime}`);
      } else {
        console.warn("⚠️ No config found, using defaults");
        setClientConfig({
          workStartTime: "09:00",
          workEndTime: "18:00",
          workingHoursPerDay: 8,
          lateThresholdMinutes: 15,
        });
      }
    } catch (error) {
      console.error("❌ Error loading client config:", error);

      // Don't show alert on auth errors - just use defaults
      if (error.response?.status !== 401 && error.response?.status !== 403) {
        showAlert(
          "تنبيه",
          language === "ar"
            ? "تم تحميل الإعدادات الافتراضية"
            : "Using default configuration",
          'warning'
        );
      }

      setClientConfig({
        workStartTime: "09:00",
        workEndTime: "18:00",
        workingHoursPerDay: 8,
        lateThresholdMinutes: 15,
      });
    } finally {
      setConfigLoading(false);
    }
  };

  // ✅ Fetch permissions for attendance module
  const fetchPermissions = async () => {
    try {
      setPermissionsLoading(true);
      const workingCompany = profile?.workingCompany || "";

      if (!workingCompany) {
        setPermissions({ modules: [], features: {} });
        setPermissionsLoading(false);
        return;
      }

      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${BASE_URL}/admin-levels/resolve/permissions?workingCompany=${encodeURIComponent(workingCompany)}`,
        { method: 'GET' }
      );
      const data = await response.json();

      setPermissions(data?.permissions || {});
    } catch (err) {
      console.error("Failed to fetch permissions:", err);
    } finally {
      setPermissionsLoading(false);
    }
  };

  // ==================== INITIALIZATION: LOAD DATA ON MOUNT ====================
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const initializeAttendanceModule = async () => {
      if (!isMounted) return;

      setIsInitializing(true);
      try {
        console.log("🚀 Initializing attendance module...");

        // Check if token exists
        const token = localStorage.getItem('token');

        if (!token) {
          console.error("❌ No authentication token found in localStorage");
          if (isMounted) {
            showAlert(

              language === "ar" ? "خطأ" : "Error",
              language === "ar"
                ? "لم يتم العثور على رمز المصادقة. يرجى تسجيل الدخول مرة أخرى"
                : "Authentication token not found. Please login again",
              'error'
            );
            setIsInitializing(false);
          }
          return;
        }

        console.log("🔑 Token found:", {
          length: token.length,
          preview: `${token.substring(0, 30)}...${token.substring(token.length - 10)}`
        });

        // Try to validate token format, but don't fail if validation has issues
        // Let the backend be the source of truth for token validity
        const isValid = validateToken();
        if (!isValid) {
          console.warn("⚠️ Token validation had issues, but proceeding with API call");
          console.warn("Backend will determine if token is truly invalid");
        }

        // Using centralized apiFetch: no axios defaults needed

        if (!isMounted) return; // Check before making API calls

        // Load initial data sequentially for proper dependency
        try {
          console.log("📡 Fetching employees data...");
          const employeesList = await loadEmployees();

          if (!isMounted) return; // Check after async operation

          if (employeesList && (employeesList.all?.length > 0 || employeesList.visible?.length > 0)) {
            console.log("📡 Fetching attendance data...");
            await loadAttendance(selectedDate, employeesList);
          } else {
            console.warn("⚠️ No employees loaded - skipping attendance fetch");
          }
        } catch (dataError) {
          if (!isMounted) return;

          console.error("❌ Error loading data:", dataError);

          // Check if it's an auth error
          if (dataError.response?.status === 401 || dataError.response?.status === 403) {
            console.error("🔒 Authentication failed - token may be invalid");
            showAlert(
              language === "ar" ? "خطأ في المصادقة" : "Authentication Error",

              language === "ar"
                ? "فشلت المصادقة. يرجى تسجيل الدخول مرة أخرى"
                : "Authentication failed. Please login again",
            );
          } else if (dataError.code === 'ECONNABORTED') {
            showAlert(

              language === "ar" ? "خطأ" : "Error",
              language === "ar"
                ? "انتهت مهلة الاتصال. يرجى المحاولة مرة أخرى"
                : "Connection timeout. Please try again",
              'warning'
            );
          } else if (dataError.message === 'Network Error') {
            showAlert(
              language === "ar" ? "خطأ" : "Error",
              language === "ar"
                ? "خطأ في الشبكة. يرجى التحقق من الاتصال"
                : "Network error. Please check your connection",
              'warning'
            );
          } else {
            // Show error for non-auth issues
            showAlert(
              language === "ar" ? "خطأ" : "Error",
              language === "ar"
                ? "فشل في تحميل البيانات الأولية"
                : "Failed to load initial data",
              'error'
            );
          }
        }

        if (isMounted) {
          console.log("✅ Attendance module initialized");
        }
      } catch (error) {
        if (!isMounted) return;

        console.error("❌ Error during initialization:", error);
        showAlert(
          language === "ar" ? "خطأ" : "Error",
          language === "ar"
            ? "فشل في تهيئة الوحدة"
            : "Failed to initialize module",
          'error'
        );
      } finally {
        if (isMounted) {
          setIsInitializing(false);
        }
      }
    };

    // Delay initialization slightly to ensure component is fully mounted
    const timer = setTimeout(initializeAttendanceModule, 200);

    return () => {
      isMounted = false; // Cleanup: mark component as unmounted
      clearTimeout(timer);
    };
  }, [language]); // Only re-run when language changes

  // ==================== RELOAD ATTENDANCE ON DATE CHANGE ====================
  useEffect(() => {
    if (!isInitializing && employees.length > 0) {
      console.log(`📅 Date changed to: ${selectedDate}`);
      setCurrentPage(1);
      loadAttendance(selectedDate, employees, 1, false);
    }
  }, [selectedDate]);

  // ==================== RESET PAGINATION ON FILTER CHANGES ====================
  useEffect(() => {
    if (currentPage !== 1) {
      console.log("🔄 Resetting pagination due to filter change");
      setCurrentPage(1);
    }
  }, [searchTerm, filterStatus]);

  // ✅ NEW: Store client configuration

  // Handle scroll lock when modals are open
  useEffect(() => {
    if (isModalOpen || showHistoryModal) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isModalOpen, showHistoryModal]);

  // ==================== HELPER: CREATE FULL DATETIME ====================
  const createFullDatetime = (date, time) => {
    const [year, month, day] = date.split("-").map(Number);
    const [hours, minutes] = time.split(":").map(Number);
    const datetime = new Date(
      Date.UTC(year, month - 1, day, hours, minutes, 0, 0)
    );
    return datetime.toISOString();
  };

  // ==================== HELPER: FORMAT ISO DATETIME TO DD/MM/YYYY HH:mm ====================
  const formatDatetime = (isoString) => {
    if (!isoString) return "-";
    try {
      const date = new Date(isoString);
      const day = String(date.getUTCDate()).padStart(2, "0");
      const month = String(date.getUTCMonth() + 1).padStart(2, "0");
      const year = date.getUTCFullYear();
      const hours = String(date.getUTCHours()).padStart(2, "0");
      const minutes = String(date.getUTCMinutes()).padStart(2, "0");
      return `${day}/${month}/${year} ${hours}:${minutes}`;
    } catch (error) {
      return isoString;
    }
  };

  const normalizeInfinityCode = (value) => {
    if (value === undefined || value === null) return null;
    const normalized = String(value).trim();
    if (!normalized || normalized === 'null' || normalized === 'undefined' || normalized === 'NaN') {
      return null;
    }
    return normalized;
  };

  const decodeTokenPayload = () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return null;
      const parts = token.split('.');
      if (parts.length !== 3) return null;
      let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (base64.length % 4) {
        base64 += '=';
      }
      const json = atob(base64);
      return JSON.parse(json);
    } catch (err) {
      console.warn('Failed to decode auth token payload for manager context', err);
      return null;
    }
  };

  const resolveManagerContext = () => {
    const payload = decodeTokenPayload();
    const storedClientName = profile?.clientName || localStorage.getItem('clientName');
    const storedManager = normalizeInfinityCode(profile?.infinityCode || localStorage.getItem('infinityCode'));
    const payloadManager = normalizeInfinityCode(payload?.infinityCode || payload?.employeeInfinityCode);
    const payloadClient = payload?.clientName || payload?.employeeClientName || payload?.employee?.clientName;

    return {
      clientName: storedClientName || payloadClient || '',
      managerCode: storedManager || payloadManager || null,
    };
  };

  const mapEmployeeRecord = (emp) => ({
    employeeId: emp?.infinityCode || emp?.id,
    name: emp?.name || emp?.sr || emp?.fullName || emp?.englishName || '',
    arabicName: emp?.arabicName || emp?.name_ar || emp?.sr || emp?.name || '',
    workingCompany: emp?.workingCompany || emp?.contractCompany || null,
  });

  const mapEmployeeList = (list) => (Array.isArray(list) ? list.map(mapEmployeeRecord) : []);

  const fetchHierarchyFallback = async ({ clientName, managerCode }) => {
    if (!clientName || !managerCode) {
      console.warn('Hierarchy fallback skipped due to missing clientName or managerCode', { clientName, managerCode });
      return [];
    }

    const candidates = Array.from(new Set([
      clientName,
      clientName.replace(/\s+/g, ''),
    ])).filter(Boolean);

    for (const candidate of candidates) {
      try {
        const hierarchyUrl = `${BASE_URL}/employees/clients/${encodeURIComponent(candidate)}/hierarchy?managerInfinityCode=${encodeURIComponent(managerCode)}`;
        const resp = await apiFetch(hierarchyUrl, {
          method: 'GET',
          headers: {
            'Accept-Language': language,
            'X-View-Type': 'manager',
          },
        });

        if (!resp.ok) {
          console.warn(`Hierarchy fallback request failed for client "${candidate}" with status ${resp.status}`);
          continue;
        }

        const json = await resp.json();
        const data = json?.hierarchy || json?.employees || json?.data || json;
        const mapped = mapEmployeeList(data);

        if (mapped.length) {
          console.log(`✅ Loaded ${mapped.length} employees via hierarchy fallback for manager ${managerCode}`);
          return mapped;
        }
      } catch (err) {
        console.warn(`Hierarchy fallback error for client "${candidate}"`, err);
      }
    }

    console.warn('Hierarchy fallback did not return any employees');
    return [];
  };

  // ==================== LOAD EMPLOYEES FOR NAME MAPPING ====================
  const loadEmployees = async (retryCount = 0) => {
    try {
      const { clientName, managerCode } = resolveManagerContext();
      const storedViewType = viewType || localStorage.getItem('managerViewType') || 'manager';
      const normalizedViewType = (storedViewType || 'manager').toLowerCase();
      const effectiveViewType = normalizedViewType === 'user' ? 'manager' : normalizedViewType;

      const usp = new URLSearchParams();
      if (clientName) usp.append('clientName', clientName);
      usp.append('viewType', effectiveViewType);
      if (effectiveViewType === 'manager' && managerCode) {
        usp.append('managerInfinityCode', managerCode);
      }

      const url = `${BASE_URL}/employees/visibility?${usp.toString()}`;
      const resp = await apiFetch(url, {
        method: 'GET',
        headers: {
          "Accept-Language": language,
          "x-view-type": effectiveViewType,
        }
      });

      if (!resp.ok) {
        console.warn('⚠️ /employees/visibility request failed:', resp.status);
        return { all: [], visible: [] };
      }

      const json = await resp.json();
      let mapped = mapEmployeeList(json.employees || json.data || json);
      let usedHierarchyFallback = false;

      const shouldFallback = mapped.length === 0 && effectiveViewType === 'manager' && retryCount === 0;

      if (shouldFallback) {
        console.warn('No employees returned from visibility endpoint, attempting hierarchy fallback');
        const fallbackMapped = await fetchHierarchyFallback({ clientName, managerCode });
        if (fallbackMapped.length) {
          mapped = fallbackMapped;
          usedHierarchyFallback = true;
        }
      }

      // Use visibility list for both name mapping and visible list
      setEmployees(mapped);
      setVisibleEmployees(mapped);

      const sourceLabel = usedHierarchyFallback ? 'hierarchy fallback' : '/employees/visibility';
      console.log(`✅ Loaded ${mapped.length} visible employees from ${sourceLabel}`);
      return { all: mapped, visible: mapped };
    } catch (error) {
      console.error("❌ Error loading visible employees:", error);
      showAlert(
        language === "ar" ? "تنبيه" : "Alert",
        language === "ar" ? "فشل في تحميل قائمة الموظفين" : "Failed to load employees",
        'warning'
      );
      return { all: [], visible: [] };
    }
  };

  // ==================== GET EMPLOYEE NAME ====================
  const getEmployeeName = (employeeId) => {
    const employee = employees.find(
      (e) => String(e.employeeId) === String(employeeId)
    );

    // Try to get the best name from mapping
    let displayName = null;
    if (employee) {
      if (language === "ar") {
        displayName = employee.arabicName && employee.arabicName.trim()
          ? employee.arabicName.trim()
          : employee.name && employee.name.trim()
            ? employee.name.trim()
            : null;
      } else {
        displayName = employee.name && employee.name.trim()
          ? employee.name.trim()
          : employee.arabicName && employee.arabicName.trim()
            ? employee.arabicName.trim()
            : null;
      }
    }

    // If not found in mapping, try to find in attendanceData (cloud fallback)
    if (!displayName || !displayName.trim()) {
      const attendanceRecord = attendanceData.find(
        (r) => String(r.employeeId) === String(employeeId)
      );
      if (attendanceRecord) {
        displayName =
          (language === "ar"
            ? attendanceRecord.employee?.arabicName || attendanceRecord.employee?.name_ar
            : attendanceRecord.employee?.name || attendanceRecord.employee?.sr || attendanceRecord.employee?.fullName || attendanceRecord.employee?.englishName)
          || attendanceRecord.employeeName
          || null;
      }
    }

    // Final fallback: just show the ID
    if (!displayName || !displayName.trim()) {
      console.warn(`⚠️ Employee name not found for ID: ${employeeId}`);
      return String(employeeId);
    }

    // Limit to first three words
    return displayName.trim().split(/\s+/).slice(0, 3).join(" ");
  };

  // ==================== CSV IMPORT: PARSE CSV ====================
  const parseCSV = (text) => {
    const lines = text.split("\n").filter((line) => line.trim());
    if (lines.length === 0) return { headers: [], rows: [] };

    const headers = lines[0].split(",").map((h) => h.trim());
    const rows = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i];
      const cells = [];
      let current = "";
      let insideQuotes = false;

      for (let j = 0; j < line.length; j++) {
        const char = line[j];
        if (char === '"') {
          insideQuotes = !insideQuotes;
        } else if (char === "," && !insideQuotes) {
          cells.push(current.trim());
          current = "";
        } else {
          current += char;
        }
      }
      cells.push(current.trim());

      const row = {};
      headers.forEach((header, idx) => {
        row[header] = cells[idx] || "";
      });
      rows.push(row);
    }

    return { headers, rows };
  };

  // ==================== CSV IMPORT: HANDLE FILE SELECTION ====================
  const handleImportFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result;
      if (typeof text !== "string") return;

      const { headers, rows } = parseCSV(text);
      setImportPreview(rows);
      setImportResult(null);
      setImportActiveTab("preview");
      setShowImportModal(true);
    };
    reader.readAsText(file);

    // Reset file input so same file can be selected again
    e.target.value = "";
  };

  // ==================== CSV IMPORT: EDIT CELL ====================
  const handleEditCell = (rowIdx, key, value) => {
    const updated = [...importPreview];
    updated[rowIdx][key] = value;
    setImportPreview(updated);
  };

  // ==================== CSV IMPORT: DELETE ROW ====================
  const handleDeleteRow = (rowIdx) => {
    const updated = importPreview.filter((_, idx) => idx !== rowIdx);
    setImportPreview(updated);
  };

  // ==================== CSV IMPORT: CLOSE AND RESET MODAL ====================
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setImportPreview([]);
    setImportResult(null);
    setImportActiveTab("preview");
    setDuplicateSelections({});
  };

  // ==================== CSV IMPORT: HANDLE DUPLICATE SELECTION ====================
  const handleDuplicateSelection = (dupIndex, selectedRecord) => {
    setDuplicateSelections((prev) => ({
      ...prev,
      [dupIndex]: selectedRecord,
    }));
  };

  // ==================== CSV IMPORT: UPDATE DUPLICATE IN DATABASE ====================
  const handleUpdateDuplicate = async (dupIndex, dup, selectedRecord) => {
    if (!selectedRecord) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "يرجى اختيار إما السجل الموجود أو السجل الجديد" : "Please select either Existing or Incoming record", 'error');
      return;
    }

    try {
      // Prepare request payload
      const payload = {
        employeeId: dup.employeeId,
        date: dup.date,
        selectedRecord: selectedRecord,
      };

      // If incoming record is selected, include incoming data
      if (selectedRecord === "incoming" && dup.incoming) {
        payload.incomingData = {
          name: dup.incoming.name,
          checkIn: dup.incoming.checkIn,
          checkOut: dup.incoming.checkOut,
          status: dup.incoming.status,
          notes: dup.incoming.notes,
          workingHours: dup.incoming.workingHours,
          overTime: dup.incoming.overTime,
          underTime: dup.incoming.underTime,
        };
      }

      // Send request to update the duplicate
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${BASE_URL}/attendance/resolve-duplicate`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
          headers: {
            "Accept-Language": language,
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        // Remove this duplicate from the list
        setImportResult((prev) => ({
          ...prev,
          duplicates: prev.duplicates.filter((_, idx) => idx !== dupIndex),
        }));

        // Remove from selections
        setDuplicateSelections((prev) => {
          const updated = { ...prev };
          delete updated[dupIndex];
          return updated;
        });

        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم حل النسخة المكررة بنجاح!" : "✅ Duplicate resolved successfully!", 'success');
      }
    } catch (error) {
      console.error("❌ Failed to resolve duplicate:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل حل النسخة المكررة. حاول مرة أخرى." : "Failed to resolve duplicate. Please try again.", 'error');
    }
  };

  // ==================== CSV IMPORT: RESOLVE ALL DUPLICATES AT ONCE ====================
  const handleResolveDuplicatesBulk = async (bulkChoice) => {
    if (!importResult?.duplicates || importResult.duplicates.length === 0) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "لا توجد نسخ مكررة للحل" : "No duplicates to resolve", 'info');
      return;
    }

    setIsResolvingDuplicates(true);
    try {
      // Build payload for all duplicates
      const duplicatesPayload = importResult.duplicates.map((dup, idx) => {
        const payload = {
          employeeId: dup.employeeId,
          date: dup.date,
          selectedRecord: bulkChoice, // "existing" or "incoming"
        };

        // If incoming record is selected, include incoming data
        if (bulkChoice === "incoming" && dup.incoming) {
          payload.incomingData = {
            name: dup.incoming.name,
            checkIn: dup.incoming.checkIn,
            checkOut: dup.incoming.checkOut,
            status: dup.incoming.status,
            notes: dup.incoming.notes,
            workingHours: dup.incoming.workingHours,
            overTime: dup.incoming.overTime,
            underTime: dup.incoming.underTime,
          };
        }

        return payload;
      });

      console.log(
        `📤 Sending ${duplicatesPayload.length} duplicate resolutions (${bulkChoice})...`
      );

      // Send bulk request
      // ✅ Use apiFetch instead of axios
      const response = await apiFetch(
        `${BASE_URL}/attendance/resolve-duplicate`,
        {
          method: 'POST',
          body: JSON.stringify({
            duplicates: duplicatesPayload,
          }),
          headers: {
            "Accept-Language": language,
          }
        }
      );
      const data = await response.json();

      if (data.success) {
        // Clear all duplicates from result
        setImportResult((prev) => ({
          ...prev,
          duplicates: [],
        }));

        // Clear selections
        setDuplicateSelections({});

        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? `تم حل جميع ${duplicatesPayload.length} النسخ المكررة بنجاح!` : `✅ All ${duplicatesPayload.length} duplicates resolved successfully!`, 'success');

        // If no more duplicates, close modal or reload
        setTimeout(() => {
          if (
            !importResult.duplicates ||
            importResult.duplicates.length === 0
          ) {
            handleCloseImportModal();
            loadAttendance(selectedDate, employees);
          }
        }, 1500);
      }
    } catch (error) {
      console.error("❌ Failed to resolve duplicates:", error);
      const errorMsg = error.response?.data?.error || error.message;
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? `فشل حل النسخ المكررة: ${errorMsg}` : `Failed to resolve duplicates: ${errorMsg}`, 'error');
    } finally {
      setIsResolvingDuplicates(false);
    }
  };

  // ==================== CSV IMPORT: SERIALIZE TO CSV ====================
  const serializePreviewToCSV = (rows) => {
    if (rows.length === 0) return "";

    const headers = Object.keys(rows[0]);
    const headerLine = headers.map((h) => `"${h}"`).join(",");

    const dataLines = rows.map((row) => {
      return headers
        .map((header) => {
          const value = row[header] || "";
          const strValue = String(value);
          // Quote fields that contain commas, quotes, or newlines
          if (
            strValue.includes(",") ||
            strValue.includes('"') ||
            strValue.includes("\n")
          ) {
            return `"${strValue.replace(/"/g, '""')}"`;
          }
          return strValue;
        })
        .join(",");
    });

    return [headerLine, ...dataLines].join("\n");
  };

  // ==================== CSV IMPORT: PROCESS IMPORT ====================
  const handleProcessImport = async () => {
    if (importPreview.length === 0) {
      showAlert(language === "ar" ? "تنبيه" : "Alert", language === "ar" ? "لا توجد صفوف للاستيراد!" : "No rows to import!", 'warning');
      return;
    }

    setIsImporting(true);
    try {
      // Serialize edited preview back to CSV (backend will validate access)
      const csvContent = serializePreviewToCSV(importPreview);
      const blob = new Blob([csvContent], { type: "text/csv" });
      const file = new File([blob], "attendance_import.csv", {
        type: "text/csv",
      });

      // Send to backend
      const formData = new FormData();
      formData.append("file", file);


      // Get viewType from state (reactive to UI changes), fallback to localStorage
      const currentViewType = viewType || localStorage.getItem('managerViewType') || 'manager';

      // Send viewType as both query param and x-view-type header for backend enforcement
      const response = await apiFetch(
        `${BASE_URL}/attendance/import/${profile?.infinityCode || localStorage.getItem("infinityCode")}?viewType=${encodeURIComponent(currentViewType)}`,
        {
          method: 'POST',
          body: formData,
          headers: {
            "Accept-Language": language,
            "x-view-type": currentViewType,
          }
        }
      );
      const responseData = await response.json();

      console.log("✅ Import response:", responseData);
      // Fallback: if responseData.details is missing, use responseData directly
      if (responseData && responseData.details) {
        setImportResult(responseData.details);
      } else {
        setImportResult(responseData);
      }
      setImportActiveTab("results");

      // Refresh attendance data after successful import
      setTimeout(() => {
        loadAttendance(selectedDate, employees);
      }, 1000);
    } catch (error) {
      console.error("❌ Import failed:", error);

      // Handle access denied errors
      if (error.response?.status === 403 || error.response?.data?.code === 'ACCESS_DENIED') {
        const backendError = error.response?.data?.error || error.message;
        showAlert(
          language === "ar" ? "رفض الوصول" : "Access Denied",
          language === "ar"
            ? `ليس لديك صلاحية لاستيراد حضور بعض الموظفين:\n\n${backendError}`
            : `You don't have permission to import attendance for some employees:\n\n${backendError}`,
          'error'
        );
      } else {
        const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message;
        showAlert(
          language === "ar" ? "خطأ" : "Error",
          language === "ar" ? `فشل الاستيراد: ${errorMsg}` : `Import failed: ${errorMsg}`,
          'error'
        );
      }
    } finally {
      setIsImporting(false);
    }
  };

  // ==================== FIXED: LOAD ATTENDANCE ====================
const loadAttendance = async (
  date = selectedDate,
  employeesList = employees,
  page = 1,
  append = false,
  forceRefresh = false
) => {
  try {
    setLoading(true);
    const storedViewType = localStorage.getItem('viewType') || 'admin';

    console.log(`👁️ Loading attendance via visibility endpoint (viewType: ${storedViewType}, date: ${date}, forceRefresh: ${forceRefresh})`);
    try {
      const url = new URL(`${BASE_URL}/attendance/visibility`);
      url.searchParams.append('date', date);
      if (forceRefresh) {
        url.searchParams.append('forceRefresh', 'true');
      }

      const resp = await apiFetch(url.toString(), {
        method: 'GET',
        headers: {
          "Accept-Language": language,
          "x-view-type": storedViewType
        },
      });

      if (!resp.ok) {
        console.warn(`Attendance visibility fetch failed: ${resp.status}`);
        setAttendanceData([]);
        return;
      }

      const json = await resp.json();
      const attendanceRecords = json?.data?.attendance || [];
      console.log(`✅ Loaded ${attendanceRecords.length} attendance records for ${date}`);

      const rows = attendanceRecords
        .map(record => {
          const employeeId = record.employeeId || record.employee?.infinityCode;
          const employeeName =
            record.employee?.sr || record.employeeName || getEmployeeName(employeeId) || String(employeeId);

          return {
            id: record.id || `${employeeId}-${date}`,
            employeeId,
            employeeName,
            clientName: record.clientName || profile?.clientName || localStorage.getItem('clientName') || "",
            workingCompany: record.workingCompany || null,
            employeeType: record.employeeType || null,
            date: date,
            checkIn: record.checkIn ? new Date(record.checkIn).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
            checkOut: record.checkOut ? new Date(record.checkOut).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' }) : '-',
            hoursWorked: record.workingHours ?? record.hoursWorked ?? '-',
            status: record.status ?? '-',
            notes: record.notes ?? '-',
            overTime: formatOvertimeUndertimeDisplay(record.overTime),
            underTime: formatOvertimeUndertimeDisplay(record.underTime),
            manuallyEdited: record.manuallyEdited ?? false,
            editedByIn: record.editedByIn ?? null,
            editedByOut: record.editedByOut ?? null,
          };
        });

      rows.sort((a, b) => (a.employeeName || '').localeCompare(b.employeeName || ''));

      if (append) {
        setAttendanceData((prev) => [...prev, ...rows]);
      } else {
        setAttendanceData(rows);
        setCurrentPage(1);
      }

      await loadAllCompanyConfigs(rows);

      console.log(`✅ Loaded ${rows.length} attendance records for ${date}`);
    } catch (err) {
      console.error(`Error fetching attendance by visibility:`, err);
      setAttendanceData([]);
    }
  } catch (error) {
    console.error('Error loading attendance:', error);
  } finally {
    setLoading(false);
  }
};

  // "Show more" / incremental loading removed - pagination uses page controls instead

  // ==================== NEW: State for multiple company configs ====================
  // Add this to your state declarations at the top of the component:
  const [companyConfigs, setCompanyConfigs] = useState({});

  // ==================== LOAD ALL COMPANY CONFIGURATIONS ====================
  const loadAllCompanyConfigs = async (attendanceRecords) => {
    try {
      const clientName = profile?.clientName || localStorage.getItem("clientName") || "Infinity";

      // Get unique working companies from attendance records
      const uniqueCompanies = [
        ...new Set(
          attendanceRecords.map((r) => r.workingCompany).filter(Boolean)
        ),
      ];

      console.log(
        `🔧 Loading configs for ${uniqueCompanies.length} companies: ${uniqueCompanies.join(", ") || "default only"}`
      );

      // Load current config + archive history in parallel for each company
      const configPromises = uniqueCompanies.map(async (companyName) => {
        try {
          // Fetch current config and history in parallel
          const [currentRes, historyRes] = await Promise.all([
            apiFetch(
              `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/${encodeURIComponent(companyName)}`,
              { method: 'GET', headers: { "Accept-Language": language } }
            ),
            apiFetch(
              `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}/${encodeURIComponent(companyName)}/archive/history?limit=100`,
              { method: 'GET', headers: { "Accept-Language": language } }
            ).catch(() => null), // history fetch is non-blocking
          ]);

          const currentData = await currentRes.json();
          const historyData = historyRes ? await historyRes.json().catch(() => null) : null;

          if (currentData.success && currentData.data) {
            // Sort history by attendanceEffectiveDate ascending (oldest first)
            const rawHistory = Array.isArray(historyData?.data) ? historyData.data : [];
            const history = [...rawHistory].sort((a, b) => {
              const aDate = a.attendanceEffectiveDate ? new Date(a.attendanceEffectiveDate) : new Date(0);
              const bDate = b.attendanceEffectiveDate ? new Date(b.attendanceEffectiveDate) : new Date(0);
              return aDate - bDate;
            });
            console.log(`   Loaded config for ${companyName} (${history.length} archived versions)`);
            return { companyName, current: currentData.data, history };
          }
        } catch (error) {
          console.error(`❌ Error loading config for ${companyName}:`, error);
        }
        return null;
      });

      const results = await Promise.all(configPromises);

      // Create config map: { companyName: { current, history } }
      const configMap = {};
      results.forEach((result) => {
        if (result) {
          configMap[result.companyName] = { current: result.current, history: result.history };
        }
      });

      // Load default config if needed (for employees without workingCompany)
      if (attendanceRecords.some((r) => !r.workingCompany)) {
        try {
          const response = await apiFetch(
            `${BASE_URL}/api/company-config/${encodeURIComponent(clientName)}`,
            { method: 'GET', headers: { "Accept-Language": language } }
          );
          const respJson = await response.json();

          if (respJson.success && respJson.data) {
            configMap["default"] = { current: respJson.data, history: [] };
          }
        } catch (error) {
          console.error("❌ Error loading default config:", error);
        }
      }

      // Ensure default fallback exists
      if (!configMap["default"]) {
        configMap["default"] = {
          current: {
            workStartTime: "09:00",
            workEndTime: "18:00",
            workingHoursPerDay: 8,
            lateThresholdMinutes: 15,
          },
          history: [],
        };
      }

      setCompanyConfigs(configMap);
      console.log(`✅ Loaded ${Object.keys(configMap).length} company configs`);

      return configMap;
    } catch (error) {
      console.error("❌ Error loading company configs:", error);
      showAlert(
        language === "ar" ? "تنبيه" : "Alert",
        language === "ar"
          ? "تم تحميل الإعدادات الافتراضية بسبب خطأ"
          : "Warning: Using default configuration",
        'warning'
      );

      const defaultConfig = {
        default: {
          workStartTime: "09:00",
          workEndTime: "18:00",
          workingHoursPerDay: 8,
          lateThresholdMinutes: 15,
        },
      };
      setCompanyConfigs(defaultConfig);
      return defaultConfig;
    }
  };

  // ==================== GET CONFIG FOR EMPLOYEE ====================
  // Returns the CURRENT config (no date awareness). Kept for backwards compat.
  const getConfigForEmployee = (workingCompany, employeeType = null) => {
    return getConfigForEmployeeOnDate(workingCompany, employeeType, null);
  };

  // ==================== DATE-AWARE CONFIG RESOLVER ====================
  /**
   * Returns the config version that was active on `recordDate`.
   * - Walks the archive history (sorted oldest→newest by attendanceEffectiveDate).
   * - The last archive entry whose attendanceEffectiveDate ≤ recordDate wins.
   * - If none matches (all archived dates are in the future) fall back to current config.
   * - If recordDate is null / undefined, always use current config.
   */
  const getConfigForEmployeeOnDate = (workingCompany, employeeType = null, recordDate = null) => {
    // Resolve the company's config bundle
    const bundle =
      (workingCompany && companyConfigs[workingCompany]) ||
      companyConfigs["default"] ||
      null;

    const fallbackRaw = {
      workStartTime: "09:00",
      workEndTime: "18:00",
      workingHoursPerDay: 8,
      lateThresholdMinutes: 15,
    };

    let baseConfig = bundle ? bundle.current : fallbackRaw;

    // ── Effective-date resolution ────────────────────────────────────────────
    if (recordDate && bundle && Array.isArray(bundle.history) && bundle.history.length > 0) {
      const target = new Date(recordDate);
      target.setUTCHours(0, 0, 0, 0);

      // history is sorted oldest→newest; find the latest entry still ≤ target
      let matched = null;
      for (const version of bundle.history) {
        if (!version.attendanceEffectiveDate) continue;
        const vDate = new Date(version.attendanceEffectiveDate);
        vDate.setUTCHours(0, 0, 0, 0);
        if (vDate <= target) {
          matched = version;
        } else {
          break; // history is sorted asc, no point continuing
        }
      }

      // Also compare against the current (live) config's effective date
      const currentEffective = bundle.current?.attendanceEffectiveDate
        ? new Date(bundle.current.attendanceEffectiveDate)
        : null;
      if (currentEffective) {
        currentEffective.setUTCHours(0, 0, 0, 0);
        if (currentEffective <= target) {
          // Current config is also applicable — it's always "newer" than archive so use it
          baseConfig = bundle.current;
        } else if (matched) {
          // Target date is before the current config's effective date → use archived version
          baseConfig = matched;
          console.log(
            `📅 Using archived config (effective ${new Date(matched.attendanceEffectiveDate).toISOString().split('T')[0]}) for date ${recordDate} / company ${workingCompany || 'default'}`
          );
        }
        // else: no history match and current is in the future → fall back to current (best effort)
      } else if (matched) {
        // Current config has no effective date; use the most recent archived match
        baseConfig = matched;
      }
    }
    // ── End effective-date resolution ────────────────────────────────────────

    // Apply department-specific rules on top of the resolved base
    if (employeeType && baseConfig.attendanceRules) {
      let attendanceRules = baseConfig.attendanceRules;
      if (typeof attendanceRules === 'string') {
        try { attendanceRules = JSON.parse(attendanceRules); } catch (e) { attendanceRules = {}; }
      }
      if (attendanceRules[employeeType]) {
        const deptRules = attendanceRules[employeeType];
        console.log(`🎯 Using dept-specific config for: ${employeeType} in ${workingCompany || 'default'}`);
        return { ...baseConfig, ...deptRules };
      }
    }

    return baseConfig;
  };

  // Format overtime/tardiness for display
  const formatOvertimeUndertimeDisplay = (value) => {
    if (!value || value === 0 || value === "0" || value === "-") {
      return "-";
    }

    if (typeof value === "string" && value.includes("m")) {
      return value;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return "-";
    if (numValue === 0) return "-";

    return `${numValue.toFixed(1)}m`;
  };

  // ==================== FORCE REFRESH WITH RECALCULATION ====================
const forceRefreshAttendance = async () => {
  setIsRecalculating(true);
  try {
    console.log("🔄 Force refreshing attendance with recalculation...");
    await loadAttendance(selectedDate, employees, 1, false, true);
    showAlert(
      language === "ar" ? "نجاح" : "Success",
      language === "ar"
        ? "تم تحديث بيانات الحضور وإعادة حسابها بنجاح!"
        : t.refreshSuccess,
      'success'
    );
  } catch (error) {
    console.error("❌ Error force refreshing attendance:", error);
    showAlert(
      language === "ar" ? "خطأ" : "Error",
      language === "ar"
        ? "فشل تحديث بيانات الحضور"
        : t.refreshFailed,
      'error'
    );
  } finally {
    setIsRecalculating(false);
  }
};
  // ==================== HELPER: NORMALIZE A RAW SERVER ATTENDANCE RECORD ====================
  // Converts ISO datetime fields to the same format used when records are first loaded:
  //   date     → "YYYY-MM-DD"
  //   checkIn  → "HH:mm"  (or "-" when null)
  //   checkOut → "HH:mm"  (or "-" when null)
  const normalizeServerRecord = (raw) => ({
    ...raw,
    date: raw.date
      ? new Date(raw.date).toISOString().split('T')[0]
      : raw.date,
    checkIn: raw.checkIn
      ? new Date(raw.checkIn).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
      : '-',
    checkOut: raw.checkOut
      ? new Date(raw.checkOut).toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit' })
      : '-',
    manuallyEdited: raw.manuallyEdited ?? false,
    editedByIn: raw.editedByIn ?? null,
    editedByOut: raw.editedByOut ?? null,
  });

  // ==================== UPDATED: UPDATE CHECK-IN (EMPLOYEE-SPECIFIC) ====================
  const updateAttendancein = async (id, record, customTime = null) => {
    try {
      const employeeConfig = getConfigForEmployeeOnDate(record.workingCompany, record.employeeType, record.date);
      const resetTime = customTime || employeeConfig.workStartTime || "09:00";
      const currentViewType = localStorage.getItem('managerViewType') || 'manager'; // ✅ renamed to avoid shadowing


      const [year, month, day] = record.date.split("-").map(Number);
      const [hours, minutes] = resetTime.split(":").map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const fullDatetime = localDate.toISOString();

      // Create a new AccessControllerEvent row with manually=true; attendance is recalculated from events
      const response = await apiFetch(`${BASE_URL}/attendance/manual-access-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
        body: JSON.stringify({ employeeNoString: record.employeeId, date: fullDatetime, type: 'in' }),
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم تحديث وقت الدخول!" : (t.checkInUpdated || "✅ Check-in time updated!"), 'success');
      } else {
        const errorData = await response.json().catch(() => ({ error: "Invalid request" }));
        showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الدخول" : (errorData.error || t.updateCheckInFailed), 'error');
      }
    } catch (error) {
      console.error("❌ Error updating check-in:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الدخول" : (t.updateCheckInFailed || `Failed to update: ${error.message}`), 'error');
    }
  };

  // ==================== UPDATED: UPDATE CHECK-OUT (EMPLOYEE-SPECIFIC) ====================
  const updateAttendanceout = async (id, record, customTime = null) => {
    try {
      const employeeConfig = getConfigForEmployeeOnDate(record.workingCompany, record.employeeType, record.date);
      const resetTime = customTime || employeeConfig.workEndTime || "18:00";
      const currentViewType = localStorage.getItem('managerViewType') || 'manager'; // ✅ renamed to avoid shadowing

      const [year, month, day] = record.date.split("-").map(Number);
      const [hours, minutes] = resetTime.split(":").map(Number);
      const localDate = new Date(year, month - 1, day, hours, minutes, 0, 0);
      const fullDatetime = localDate.toISOString();

      // Create a new AccessControllerEvent row with manually=true; attendance is recalculated from events
      const response = await apiFetch(`${BASE_URL}/attendance/manual-access-event`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Cache-Control": "no-cache",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
        body: JSON.stringify({ employeeNoString: record.employeeId, date: fullDatetime, type: 'out' }),
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(language === "ar" ? "نجاح" : "Success", language === "ar" ? "تم تحديث وقت الخروج!" : (t.checkOutUpdated || "✅ Check-out time updated!"), 'success');
      } else {
        const errorData = await response.json().catch(() => ({ error: "Invalid request" }));
        showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الخروج" : (errorData.error || t.updateCheckOutFailed), 'error');
      }
    } catch (error) {
      console.error("❌ Error updating check-out:", error);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل تحديث وقت الخروج" : (t.updateCheckOutFailed || `Failed to update: ${error.message}`), 'error');
    }
  };

  // ==================== HELPER: CREATE FULL DATETIME WITH EGYPT TIMEZONE (GMT+2) ====================
  const createFullDatetimeWithTimezone = (date, time) => {
    const [year, month, day] = date.split("-").map(Number);
    const [hours, minutes] = time.split(":").map(Number);

    // Create date in Egypt timezone (GMT+2)
    const egyptDate = new Date(year, month - 1, day, hours, minutes, 0, 0);

    return egyptDate.toISOString();
  };

  // ==================== REVERT ATTENDANCE TO ORIGINAL ====================
  const revertAttendanceToOriginal = async (record) => {
    try {
      const currentViewType = localStorage.getItem('managerViewType') || 'manager';
      const response = await apiFetch(`${BASE_URL}/attendance/${record.id}/revert`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Accept-Language": language,
          "x-view-type": currentViewType,
        },
      });

      if (response.ok) {
        await forceRefreshAttendance();
        showAlert(
          language === "ar" ? "نجاح" : "Success",
          language === "ar" ? "تم استعادة البيانات الأصلية بنجاح!" : "✅ Attendance reverted to original fingerprint data!",
          'success'
        );
      } else {
        const errorData = await response.json().catch(() => ({ error: "Failed to revert" }));
        showAlert(
          language === "ar" ? "خطأ" : "Error",
          language === "ar" ? "فشل استعادة البيانات الأصلية" : (errorData.error || "❌ Failed to revert attendance"),
          'error'
        );
      }
    } catch (error) {
      console.error("❌ Error reverting attendance:", error);
      showAlert(
        language === "ar" ? "خطأ" : "Error",
        language === "ar" ? "فشل استعادة البيانات الأصلية" : `❌ Failed to revert: ${error.message}`,
        'error'
      );
    }
  };

  // ==================== FETCH EMPLOYEE HISTORY ====================
  const fetchEmployeeHistory = async (employeeId) => {
    try {
      const res = await apiFetch(
        `${BASE_URL}/attendance/employee/month/${employeeId}`,
        {
          method: 'GET',
          headers: {
            "Accept-Language": language,
          },
        }
      );
      const data = await res.json();
      setEmployeeHistory(data);
      setSelectedEmployee(getEmployeeName(employeeId));
      setShowHistoryModal(true);
    } catch (err) {
      console.error("Failed to fetch employee history:", err);
      showAlert(language === "ar" ? "خطأ" : "Error", language === "ar" ? "فشل في جلب سجل الموظف" : "Failed to fetch employee history", 'error');
    }
  };

  // ==================== EXPORT TO CSV ====================
  const exportToCSV = () => {
    if (!filteredAttendance.length) {
      showAlert(language === "ar" ? "تنبيه" : "Warning", language === "ar" ? "لا توجد بيانات للتصدير!" : t.noDataExport, 'warning');
      return;
    }

    const headers = [
      t.employeeID,
      t.employeeName,
      t.client,
      t.date,
      t.checkIn,
      t.checkOut,
      t.hoursWorked,
      t.status,
      t.overtime,
      t.tardiness,
      language === "ar" ? "ملاحظات" : "Notes",
    ];

    const rows = filteredAttendance.map((record) => [
      record.employeeId,
      record.employeeName,
      record.clientName || "-",
      record.date,
      record.checkIn || "-",
      record.checkOut || "-",
      record.hoursWorked > 0 ? `${record.hoursWorked} ${t.hours}` : "-",
      getStatusText(record.status),
      record.overTime || "-",
      record.underTime || "-",
      record.notes || "-",
    ]);

    const csvContent = [headers, ...rows].map((e) => e.join(",")).join("\n");
    const blob = new Blob(["\uFEFF" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `attendance_${selectedDate}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ==================== EXPORT TEMPLATE ====================
  const handleExportTemplate = () => {
    const link = document.createElement("a");
    link.href = templateFile;
    link.download = "attendance_template.xlsx";
    link.click();
  };

  // ==================== CALCULATE STATS ====================
  const getAttendanceStats = (date = selectedDate) => {
    const dayAttendance = attendanceData.filter(
      (record) => record.date === date
    );

    return {
      totalEmployees: dayAttendance.length,
      present: dayAttendance.filter((r) => r.status === "present").length,
      absent: dayAttendance.filter((r) => r.status === "absent").length,
      late: dayAttendance.filter((r) => r.status === "late").length,
      onLeave: dayAttendance.filter((r) => {
        const leaveStatuses = ["onLeave", "leave", "on_leave", "On Leave", "في إجازة"];
        return leaveStatuses.includes((r.status || "").trim());
      }).length,
      presentPercentage:
        employees.length > 0
          ? Math.round(
            (dayAttendance.filter(
              (r) => r.status === "present" || r.status === "late"
            ).length /
              employees.length) *
            100
          )
          : 0,
    };
  };

  const stats = getAttendanceStats(selectedDate);

  // ==================== FILTER ATTENDANCE - FIXED ====================
  const filteredAttendance = attendanceData.filter((record) => {
    // التأكد من أن employeeName هو نص صالح
    const name =
      typeof record.employeeName === "string"
        ? record.employeeName
        : String(record.employeeName || "");
    const id = String(record.employeeId || "");
    const searchValue = String(searchTerm || "").trim();

    // إذا كان البحث فارغ، تجاهل البحث
    if (!searchValue) {
      let matchesStatus;
      if (filterStatus === "all") {
        matchesStatus = true;
      } else if (filterStatus === "onLeave") {
        // Match all common leave status variants
        const leaveStatuses = [
          "onLeave", "leave", "on_leave", "On Leave", "في إجازة"
        ];
        matchesStatus = leaveStatuses.includes((record.status || "").trim());
      } else {
        matchesStatus = record.status === filterStatus;
      }
      return matchesStatus;
    }

    // دالة مساعدة للبحث الآمن (تعمل مع الإنجليزية والعربية)
    const normalizeAndSearch = (text, query) => {
      // تطبيع النص للمقارنة (بدون تحويل الحالة للحروف العربية)
      const normalizedText = text.trim();
      const normalizedQuery = query.trim();
      // للحروف الإنجليزية نستخدم toLowerCase، للعربية ستعمل بدونها
      return normalizedText.toLowerCase().includes(normalizedQuery.toLowerCase());
    };

    // البحث في الاسم والرقم الوظيفي
    const nameMatches = normalizeAndSearch(name, searchValue);
    const idMatches = normalizeAndSearch(id, searchValue);
    const matchesSearch = nameMatches || idMatches;
    let matchesStatus;
    if (filterStatus === "all") {
      matchesStatus = true;
    } else if (filterStatus === "onLeave") {
      const leaveStatuses = [
        "onLeave", "leave", "on_leave", "On Leave", "في إجازة"
      ];
      matchesStatus = leaveStatuses.includes((record.status || "").trim());
    } else {
      matchesStatus = record.status === filterStatus;
    }

    // ✅ Debug logging - لكل record يتم البحث عن
    if (searchValue && (nameMatches || idMatches)) {
      console.log(`✅ Match: name="${name}" id="${id}" for search="${searchValue}"`);
    }

    return matchesSearch && matchesStatus;
  });

  // ==================== PAGINATE FILTERED RESULTS ====================
  const paginatedAttendance = filteredAttendance;

  // ==================== STATUS BADGE STYLING ====================
  const getStatusBadgeStyle = (status) => {
    const baseStyle = {
      padding: "4px 12px",
      borderRadius: "20px",
      fontSize: "12px",
      fontWeight: "600",
      textAlign: "center",
      display: "inline-block",
    };

    switch (status) {
      case "present":
        return { ...baseStyle, background: "#b9fbc0", color: "#087e2a" }; // fresh green
      case "absent":
        return { ...baseStyle, background: "#ffd6d6", color: "#d7263d" }; // modern red
      case "late":
        return { ...baseStyle, background: "#fff2cc", color: "#b26a00" }; // gold yellow
      case "rest_day":
      case "Rest Day":
        return { ...baseStyle, background: "#e0e7ff", color: "#3730a3" }; // soft blue
      case "holiday":
        return { ...baseStyle, background: "#ffe0f0", color: "#c026d3" }; // pink
      case "On Leave":
      case "leave":
        return { ...baseStyle, background: "#d1c4e9", color: "#512da8" }; // lavender purple
      case "half_day":
        return { ...baseStyle, background: "#ffe4b5", color: "#b45309" }; // light orange
      default:
        return { ...baseStyle, background: "#e0e7ef", color: "#334155" };
    }
  };

  const getStatusText = (status) => {
    // Normalize status keys to match translation keys
    let key = status;
    if (key === "rest_day" || key === "Rest Day") key = "rest_day";
    if (key === "onLeave" || key === "leave" || key === "On Leave") key = "onLeave";
    if (key === "half_day") key = "half_day";
    if (key === "holiday") key = "holiday";
    if (key === "present") key = "present";
    if (key === "absent") key = "absent";
    if (key === "late") key = "late";
    // Use translations object for both languages
    return translations[language]?.[key] ?? status ?? "-";
  };

  // ==================== PERMISSION HELPERS ====================
  const canImportAttendance = isAdminView;
  const canEditAttendance = isAdminView || isManagerView;
  const canViewAttendance = true;

  const isDateOlderThanSevenDays = (dateStr) => {
    const recordDate = new Date(dateStr);
    const today = new Date();
    const diffTime = Math.abs(today - recordDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays > 7;
  };

  const isSameDayAsToday = (dateStr) => {
    const recordDate = new Date(dateStr);
    const today = new Date();
    recordDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return recordDate.getTime() === today.getTime();
  };

  // ==================== OVERTIME/TARDINESS DISPLAY HELPER ====================
  const getOvertimeDisplay = (value) => {
    if (!value || value === "-" || value === "0h" || value === "0m") {
      return <span style={{ color: "#9ca3af" }}>-</span>;
    }
    return (
      <span style={{ color: "#10b981", fontWeight: "600" }}>+{value}</span>
    );
  };

  const getTardinessDisplay = (value) => {
    if (!value || value === "-" || value === "0h" || value === "0m") {
      return <span style={{ color: "#9ca3af" }}>-</span>;
    }
    return (
      <span style={{ color: "#ef4444", fontWeight: "600" }}>-{value}</span>
    );
  };

  return (
    <div style={{
      background: theme === "dark" ? "#0f172a" : "#f8fafc",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      minHeight: "100vh",
      //padding: "2px",
      direction: isRTL ? "rtl" : "ltr",
    }}>
      <div style={{
      background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      //border: theme === "light" ? "1px solid rgba(0, 0, 0, 0.1)" : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        "1rem",
      padding: "30px",
      marginTop: -40,
      marginBottom: "70px",
      //backdropFilter: "blur(0px)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      }}>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2px",
            flexWrap: "wrap",
            gap: "16px",
            flexDirection: isRTL ? "row" : "row",
          }}
        >

          <div>
            <h2
              style={{
                fontSize: "clamp(20px, 4vw, 28px)",
                fontWeight: "700",
                margin: 0,
                display: "flex",
              }}
            >
              {t.attendanceManagement}
            </h2>
          </div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "10px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >

          </div>
        </div>
      </div>
      {/* Controls Section */}
      <div style={{
         background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      //border: theme === "light" ? "1px solid rgba(0, 0, 0, 0.1)" : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        "1rem",
      padding: "30px",
      marginTop: -40,
      marginBottom: "70px",
      //backdropFilter: "blur(0px)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      }}>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
            flexWrap: "wrap",
            gap: "16px",
            flexDirection: isRTL ? "row" : "row",
          }}
        >

          <div>
            <h3
              style={{
                fontSize: "clamp(16px, 3vw, 20px)",
                fontWeight: "700",
                margin: 0,
                display: "flex",
                alignItems: "center",
                gap: "10px",
                flexDirection: isRTL ? "row" : "row",
              }}
            >
              <Settings size={20} />
              {t.filtersActions}
            </h3>
          </div>
          <div
            style={{
              fontSize: "14px",
              color: theme === "dark" ? "rgba(255,255,255,0.7)" : "#6b7280",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              flexDirection: isRTL ? "row" : "row",
            }}
          >
            <BarChart3 size={16} />
            {t.showing} {filteredAttendance.length} {t.of}{" "}
            {attendanceData.length} {t.records}
          </div>
        </div>

        {/* Search and Filters */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
            marginBottom: "20px",
          }}
        >
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                flexDirection: isRTL ? "row" : "row",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <Search size={16} />
              {t.searchEmployee}
            </label>
            <input
              type="text"
              placeholder={t.typeEmployeeName}
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value;
                setSearchTerm(value);
                console.log(`🔍 Searching for: "${value}"`);
                console.log(`📊 Available records: ${attendanceData.length}`);
              }}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "12px",
                border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(148, 163, 184, 0.3)",
                background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
                color: theme === "dark" ? "#f1f5f9" : "#334155",
                textAlign: isRTL ? "right" : "left",
                direction: isRTL ? "rtl" : "ltr",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.3s ease",
              }}
            />
          </div>

          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                flexDirection: "row",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <Filter size={16} />
              {t.statusFilter}
            </label>
            <div style={{ position: 'relative' }}>
              <ReusableDropdown
                items={[
                  { key: 'all', label: t.allStatuses },
                  { key: 'present', label: t.presentOnly },
                  { key: 'absent', label: t.absentOnly },
                  { key: 'late', label: t.lateOnly },
                  { key: 'onLeave', label: t.onLeave }
                ]}
                value={statusSearch}
                onChange={(val) => setStatusSearch(val)}
                onSelect={(item) => {
                  setFilterStatus(item.key);
                  setStatusSearch(item.label);
                }}
                onClear={() => {
                  setStatusSearch('');
                  setFilterStatus('all');
                }}
                placeholder={t.statusFilter}
                getItemKey={(it) => it.key}
                getItemLabel={(it) => it.label}
                theme={theme}
                language={language}
                maxItems={10}
                showAllOnArrowClick={true}
              />
            </div>
          </div>
          <div>
            <label
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "600",
                flexDirection: isRTL ? "row" : "row",
                textAlign: isRTL ? "right" : "left",
              }}
            >
              <Calendar size={16} />
              {t.selectDate}
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              style={{
                width: "100%",
                padding: "12px 16px",
                borderRadius: "12px",
                border: theme === "dark" ? "1px solid rgba(148, 163, 184, 0.3)" : "1px solid rgba(148, 163, 184, 0.3)",
                background: theme === "dark" ? "rgba(71, 85, 105, 0.4)" : "rgba(226, 232, 240, 0.6)",
                color: theme === "dark" ? "#f1f5f9" : "#334155",
                direction: "ltr",
                fontSize: "14px",
                fontWeight: "500",
                transition: "all 0.3s ease",
              }}
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            paddingTop: "16px",
            borderTop:
              theme === "dark"
                ? "1px solid rgba(255,255,255,0.1)"
                : "1px solid rgba(0,0,0,0.1)",
            flexDirection: isRTL ? "row" : "row",
          }}
        >
          <button
            onClick={forceRefreshAttendance}
            disabled={isRecalculating}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)",
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              fontWeight: "500",
              cursor: isRecalculating ? "not-allowed" : "pointer",
              flexDirection: isRTL ? "row-reverse" : "row",
              opacity: isRecalculating ? 0.6 : 1,
              fontSize: "13px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              if (!isRecalculating) {
                e.currentTarget.style.background = theme === "dark"
                  ? "rgba(30, 41, 59, 1)"
                  : "rgba(51, 65, 85, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)";
            }}
          >
            <RotateCw
              size={16}
              className={isRecalculating ? "animate-spin" : ""}
            />
            {isRecalculating ? t.recalculating : t.refresh}
          </button>

          <button
            onClick={exportToCSV}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)",
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              fontWeight: "500",
              cursor: "pointer",
              flexDirection: isRTL ? "row-reverse" : "row",
              fontSize: "13px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme === "dark"
                ? "rgba(30, 41, 59, 1)"
                : "rgba(51, 65, 85, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)";
            }}
          >
            <Download size={16} />
            {t.exportCSV}
          </button>

          <button
            onClick={handleExportTemplate}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)",
              color: theme === "dark" ? "#f1f5f9" : "#1e293b",
              fontWeight: "500",
              cursor: "pointer",
              flexDirection: isRTL ? "row-reverse" : "row",
              fontSize: "13px",
              transition: "all 0.2s ease",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = theme === "dark"
                ? "rgba(30, 41, 59, 1)"
                : "rgba(51, 65, 85, 0.2)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)";
            }}
          >
            <Download size={16} />
            {t.exportTemplate}
          </button>

          <button
            onClick={() => document.getElementById("csv-import-input").click()}
            disabled={!canEditAttendance}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 16px",
              borderRadius: "8px",
              border: "1px solid rgba(255,255,255,0.15)",
              background: !canEditAttendance
                ? (theme === "dark" ? "rgba(100, 116, 139, 0.3)" : "rgba(203, 213, 225, 0.3)")
                : (theme === "dark"
                  ? "rgba(30, 41, 59, 0.8)"
                  : "rgba(51, 65, 85, 0.12)"),
              color: !canEditAttendance
                ? (theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)")
                : (theme === "dark" ? "#f1f5f9" : "#1e293b"),
              fontWeight: "500",
              cursor: !canEditAttendance ? "not-allowed" : "pointer",
              flexDirection: isRTL ? "row-reverse" : "row",
              fontSize: "13px",
              transition: "all 0.2s ease",
              opacity: !canEditAttendance ? 0.6 : 1,
            }}
            onMouseEnter={(e) => {
              if (canEditAttendance) {
                e.currentTarget.style.background = theme === "dark"
                  ? "rgba(30, 41, 59, 1)"
                  : "rgba(51, 65, 85, 0.2)";
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = !canEditAttendance
                ? (theme === "dark" ? "rgba(100, 116, 139, 0.3)" : "rgba(203, 213, 225, 0.3)")
                : (theme === "dark"
                  ? "rgba(30, 41, 59, 0.8)"
                  : "rgba(51, 65, 85, 0.12)");
            }}
          >
            <Upload size={16} />
            {language === "ar" ? "استيراد CSV" : "Import CSV"}
            <input
              id="csv-import-input"
              type="file"
              accept=".csv"
              onChange={handleImportFileChange}
              disabled={!canEditAttendance}
              style={{ display: "none" }}
            />
          </button>

          {canEditAttendance && (
            <LocationScheduleImport
              theme={theme}
              language={language}
              isRTL={isRTL}
            />
          )}

          {canEditAttendance && (
            <DefaultLocationModal
              theme={theme}
              language={language}
              isRTL={isRTL}
            />
          )}

        </div>
      </div>


      {/* Attendance Table */}
      <div style={{
         background:
        theme === "dark" ? "rgba(255,255,255,0.02)" : "rgba(255,255,255,0.9)",
      //border: theme === "light" ? "1px solid rgba(0, 0, 0, 0.1)" : "1px solid rgba(255, 255, 255, 0.1)",
      borderRadius:
        "1rem",
      padding: "30px",
      marginTop: -40,
      marginBottom: "70px",
      //backdropFilter: "blur(0px)",
      boxShadow:
        theme === "dark"
          ? "0 0.5rem 2rem rgba(0,0,0,0.3), inset 0 0.0625rem 0 rgba(255,255,255,0.1)"
          : "0 0.5rem 2rem rgba(0,0,0,0.1)",
      backdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      WebkitBackdropFilter: theme === "dark" ? "blur(0.625rem)" : "blur(0px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255,255,255,0.15)"
          : "1px solid rgba(0,0,0,0.05)",
      }}>

        {/* Status Filter Buttons */}
        <h3
          style={{
            fontSize: "18px",
            fontWeight: "700",
            marginBottom: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            flexDirection: isRTL ? "row" : "row",
            textAlign: isRTL ? "right" : "left",
          }}
        >
          <Calendar size={20} />
          {t.dailyAttendance}
        </h3>

        <div
          style={{
            display: "flex",
            gap: "10px",
            flexWrap: "wrap",
            flexDirection: isRTL ? "row" : "row",
          }}
        >
          <button
            onClick={() => setFilterStatus("all")}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background:
                filterStatus === "all"
                  ? "linear-gradient(135deg, #2e7cba, #2e7cba)"
                  : theme === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
              color:
                filterStatus === "all"
                  ? "#fff"
                  : theme === "dark"
                    ? "#fff"
                    : "#374151",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {t.all} ({attendanceData.length})
          </button>

          <button
            onClick={() => setFilterStatus("present")}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background:
                filterStatus === "present"
                  ? "linear-gradient(135deg, #10b981, #059669)"
                  : theme === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
              color:
                filterStatus === "present"
                  ? "#fff"
                  : theme === "dark"
                    ? "#fff"
                    : "#374151",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {t.present} ({stats.present})
          </button>

          <button
            onClick={() => setFilterStatus("absent")}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background:
                filterStatus === "absent"
                  ? "linear-gradient(135deg, #ef4444, #dc2626)"
                  : theme === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
              color:
                filterStatus === "absent"
                  ? "#fff"
                  : theme === "dark"
                    ? "#fff"
                    : "#374151",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {t.absent} ({stats.absent})
          </button>

          <button
            onClick={() => setFilterStatus("late")}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background:
                filterStatus === "late"
                  ? "linear-gradient(135deg, #f59e0b, #d97706)"
                  : theme === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
              color:
                filterStatus === "late"
                  ? "#fff"
                  : theme === "dark"
                    ? "#fff"
                    : "#374151",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {t.late} ({stats.late})
          </button>
          <button
            onClick={() => setFilterStatus("onLeave")}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background:
                filterStatus === "onLeave"
                  ? "linear-gradient(135deg, #6366f1, #818cf8)"
                  : theme === "dark"
                    ? "rgba(255,255,255,0.08)"
                    : "rgba(0,0,0,0.05)",
              color:
                filterStatus === "onLeave"
                  ? "#fff"
                  : theme === "dark"
                    ? "#fff"
                    : "#374151",
              fontWeight: "600",
              cursor: "pointer",
              transition: "all 0.3s ease",
            }}
          >
            {t.onLeave} ({stats.onLeave || attendanceData.filter(r => r.status === "onLeave").length})
          </button>
        </div>

        <div style={{ marginTop: "16px", overflow: "auto", borderRadius: "12px" }}>
          {filteredAttendance.length > 0 ? (
            <table
              style={{
                width: "100%",
                borderCollapse: "separate",
                borderSpacing: "0",
                direction: isRTL ? "rtl" : "ltr",
                minWidth: "900px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <thead>
                <tr>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      borderTopLeftRadius: isRTL ? "0px" : "12px",
                      borderTopRightRadius: isRTL ? "12px" : "0px",
                    }}
                  >
                    {t.employeeID}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.employeeName}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.date}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.checkIn}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.checkOut}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.hoursWorked}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.status}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.overtime}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.tardiness}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                    }}
                  >
                    {t.client}
                  </th>
                  <th
                    style={{
                      padding: "14px 16px",
                      textAlign: "center",
                      fontSize: "12px",
                      fontWeight: "700",
                      letterSpacing: "0.05em",
                      textTransform: "uppercase",
                      background:
                        theme === "dark"
                          ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                          : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                      borderBottom:
                        theme === "dark"
                          ? "2px solid rgba(59, 130, 246, 0.3)"
                          : "2px solid rgba(59, 130, 246, 0.1)",
                      whiteSpace: "nowrap",
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      backdropFilter: "blur(0.625rem)",
                      boxShadow:
                        theme === "dark"
                          ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                          : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                      transition: "all 0.3s ease",
                      borderTopLeftRadius: isRTL ? "12px" : "0px",
                      borderTopRightRadius: isRTL ? "0px" : "12px",
                    }}
                  >
                    {t.actions}
                  </th>
                </tr>
              </thead>
              <tbody>
                {paginatedAttendance.map((record) => (
                  <tr
                    key={record.id}
                    style={{
                      borderBottom:
                        theme === "dark"
                          ? "1px solid rgba(59, 130, 246, 0.1)"
                          : "1px solid rgba(59, 130, 246, 0.05)",
                      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background =
                        theme === "dark"
                          ? "rgba(59, 130, 246, 0.08)"
                          : "rgba(59, 130, 246, 0.04)";
                      e.currentTarget.style.boxShadow =
                        theme === "dark"
                          ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
                          : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      {record.employeeId}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        fontWeight: "500",
                      }}
                    >
                      {/**  moaty Always resolve name from employees list when possible to avoid showing id or other values */}
                      {getEmployeeName(record.employeeId) || record.employeeName || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      {new Date(record.date).toLocaleDateString(
                        language === "ar" ? "ar-EG" : "en-US",
                        {
                          year: "numeric",
                          month: "long",
                          day: "numeric",
                        }
                      )}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        direction: "ltr",
                        fontFamily: "'Courier New', monospace",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                        <span>{record.checkIn || "-"}</span>
                        {record.editedByIn && (
                          <span title={language === "ar" ? `عدّل بواسطة: ${record.editedByIn}` : `Edited by: ${record.editedByIn}`} style={{
                            fontSize: "10px",
                            color: "#f59e0b",
                            fontFamily: "sans-serif",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                          }}>
                            ✏️ {record.editedByIn}
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        direction: "ltr",
                        fontFamily: "'Courier New', monospace",
                      }}
                    >
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                        <span>{record.checkOut || "-"}</span>
                        {record.editedByOut && (
                          <span title={language === "ar" ? `عدّل بواسطة: ${record.editedByOut}` : `Edited by: ${record.editedByOut}`} style={{
                            fontSize: "10px",
                            color: "#f59e0b",
                            fontFamily: "sans-serif",
                            display: "flex",
                            alignItems: "center",
                            gap: "3px",
                          }}>
                            ✏️ {record.editedByOut}
                          </span>
                        )}
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        fontWeight: "500",
                      }}
                    >
                      {record.hoursWorked > 0
                        ? `${record.hoursWorked} ${t.hours}`
                        : "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      <span style={getStatusBadgeStyle(record.status)}>
                        {getStatusText(record.status)}
                      </span>
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        fontWeight: "500",
                      }}
                    >
                      {getOvertimeDisplay(record.overTime)}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                        fontWeight: "500",
                      }}
                    >
                      {getTardinessDisplay(record.underTime)}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        color:
                          theme === "dark"
                            ? "rgba(255,255,255,0.85)"
                            : "#475569",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      {record.clientName || "-"}
                    </td>
                    <td
                      style={{
                        padding: "14px 16px",
                        fontSize: "13px",
                        textAlign: "center",
                        borderBottom:
                          theme === "dark"
                            ? "1px solid rgba(59, 130, 246, 0.1)"
                            : "1px solid rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "6px",
                          flexDirection: isRTL ? "row-reverse" : "row",
                          justifyContent: "center",
                          flexWrap: "nowrap",
                        }}
                      >
                        {canEditAttendance && (
                          <>
                            {(() => {
                              const isAdminView = viewType === 'admin';
                              const isTooOld = !isAdminView && isDateOlderThanSevenDays(record.date);
                              const isDisabled = Object.keys(companyConfigs).length === 0 || isTooOld;
                              const disabledReason = isTooOld ? t.dateTooOldTooltip : '';
                              return (
                                <button
                                  onClick={() => updateAttendancein(record.id, record)}
                                  title={isDisabled ? disabledReason : `${t.resetCheckIn} (${getConfigForEmployee(record.workingCompany, record.employeeType)
                                    ?.workStartTime || "09:00"
                                    })`}
                                  disabled={isDisabled}
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background:
                                      isDisabled
                                        ? theme === "dark"
                                          ? "rgba(51, 65, 85, 0.5)"
                                          : "rgba(241, 245, 249, 0.7)"
                                        : theme === "dark"
                                          ? "rgba(71, 85, 105, 0.6)"
                                          : "rgba(226, 232, 240, 0.8)",
                                    color:
                                      isDisabled
                                        ? theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"
                                        : theme === "dark" ? "#f1f5f9" : "#334155",
                                    cursor:
                                      isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isDisabled
                                        ? 0.6
                                        : 1,
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isDisabled) {
                                      e.currentTarget.style.background = theme === "dark"
                                        ? "rgba(71, 85, 105, 0.8)"
                                        : "rgba(203, 213, 225, 0.8)";
                                      e.currentTarget.style.transform = "translateY(-2px)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = isDisabled
                                      ? theme === "dark"
                                        ? "rgba(51, 65, 85, 0.5)"
                                        : "rgba(241, 245, 249, 0.7)"
                                      : theme === "dark"
                                        ? "rgba(71, 85, 105, 0.6)"
                                        : "rgba(226, 232, 240, 0.8)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                  }}
                                >
                                  <RotateCcw size={14} />
                                  {/* {!isRTL && "Check In"} */}
                                </button>
                              );
                            })()}

                            {(() => {
                              const isAdminView = viewType === 'admin';
                              const isTooOld = !isAdminView && isDateOlderThanSevenDays(record.date);
                              const isDisabled = Object.keys(companyConfigs).length === 0 || isTooOld;
                              const disabledReason = isTooOld ? t.dateTooOldTooltip : '';
                              return (
                                <button
                                  onClick={() => updateAttendanceout(record.id, record)}
                                  title={isDisabled ? disabledReason : `${t.resetCheckOut} (${getConfigForEmployee(record.workingCompany, record.employeeType)
                                    ?.workEndTime || "18:00"
                                    })`}
                                  disabled={isDisabled}
                                  style={{
                                    padding: "8px 12px",
                                    borderRadius: "8px",
                                    border: "none",
                                    background:
                                      isDisabled
                                        ? theme === "dark"
                                          ? "rgba(51, 65, 85, 0.5)"
                                          : "rgba(241, 245, 249, 0.7)"
                                        : theme === "dark"
                                          ? "rgba(71, 85, 105, 0.6)"
                                          : "rgba(226, 232, 240, 0.8)",
                                    color:
                                      isDisabled
                                        ? theme === "dark" ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.5)"
                                        : theme === "dark" ? "#f1f5f9" : "#334155",
                                    cursor:
                                      isDisabled
                                        ? "not-allowed"
                                        : "pointer",
                                    opacity:
                                      isDisabled
                                        ? 0.6
                                        : 1,
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    transition: "all 0.2s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!isDisabled) {
                                      e.currentTarget.style.background = theme === "dark"
                                        ? "rgba(71, 85, 105, 0.8)"
                                        : "rgba(203, 213, 225, 0.8)";
                                      e.currentTarget.style.transform = "translateY(-2px)";
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.background = isDisabled
                                      ? theme === "dark"
                                        ? "rgba(51, 65, 85, 0.5)"
                                        : "rgba(241, 245, 249, 0.7)"
                                      : theme === "dark"
                                        ? "rgba(71, 85, 105, 0.6)"
                                        : "rgba(226, 232, 240, 0.8)";
                                    e.currentTarget.style.transform = "translateY(0)";
                                  }}
                                >
                                  <RotateCw size={14} />
                                  {/* {!isRTL && "Check Out"} */}
                                </button>
                              );
                            })()}
                          </>
                        )}

                        {canEditAttendance && isAdminView && !!(record.editedByIn || record.editedByOut) && (
                          <button
                            onClick={() => {
                              showAlert(
                                language === "ar" ? "تأكيد الاستعادة" : "Confirm Revert",
                                language === "ar"
                                  ? "هل تريد استعادة البيانات الأصلية من جهاز البصمة؟ سيتم حذف التعديلات اليدوية."
                                  : "Revert to original fingerprint data? Manual edits will be removed.",
                                'warning',
                                () => revertAttendanceToOriginal(record),
                                true
                              );
                            }}
                            title={language === "ar" ? "استعادة البيانات الأصلية" : "Revert to original"}
                            style={{
                              padding: "8px 12px",
                              borderRadius: "8px",
                              border: "none",
                              background: theme === "dark" ? "rgba(239, 68, 68, 0.25)" : "rgba(254, 226, 226, 0.9)",
                              color: theme === "dark" ? "#fca5a5" : "#b91c1c",
                              cursor: "pointer",
                              fontSize: "12px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                              transition: "all 0.2s ease",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = theme === "dark" ? "rgba(239, 68, 68, 0.4)" : "rgba(254, 202, 202, 0.9)";
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = theme === "dark" ? "rgba(239, 68, 68, 0.25)" : "rgba(254, 226, 226, 0.9)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                          >
                            <WandSparkles size={14} />
                          </button>
                        )}

                        {canViewAttendance && (
                          <button
                            onClick={() =>
                              fetchEmployeeHistory(record.employeeId)
                            }
                            style={{
                              padding: "8px 12px",
                              borderRadius: "8px",
                              border: "none",
                              background:
                                theme === "dark"
                                  ? "rgba(71, 85, 105, 0.6)"
                                  : "rgba(226, 232, 240, 0.8)",
                              color: theme === "dark" ? "#f1f5f9" : "#334155",
                              cursor: "pointer",
                              transition: "all 0.2s ease",
                              fontSize: "12px",
                              fontWeight: "600",
                              display: "flex",
                              alignItems: "center",
                              gap: "6px",
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.background = theme === "dark"
                                ? "rgba(71, 85, 105, 0.8)"
                                : "rgba(203, 213, 225, 0.8)";
                              e.currentTarget.style.transform = "translateY(-2px)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.background = theme === "dark"
                                ? "rgba(71, 85, 105, 0.6)"
                                : "rgba(226, 232, 240, 0.8)";
                              e.currentTarget.style.transform = "translateY(0)";
                            }}
                            title={t.viewHistory}
                          >
                            <Eye size={14} />
                            {/* {!isRTL && "History"} */}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}

              </tbody>
            </table>
          ) : (
            <div
              style={{
                padding: "40px",
                textAlign: "center",
                color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
              }}
            >
              <UserCheck
                size={60}
                style={{ margin: "0 auto 16px", opacity: 0.6 }}
              />
              <h3
                style={{
                  fontSize: "20px",
                  fontWeight: "700",
                  margin: "0 0 8px 0",
                  color: theme === "dark" ? "rgba(255,255,255,0.8)" : "#374151",
                }}
              >
                {t.noRecordsFound}
              </h3>
              <p
                style={{
                  fontSize: "14px",
                  opacity: 0.8,
                  color: theme === "dark" ? "rgba(255,255,255,0.6)" : "#6b7280",
                }}
              >
                {searchTerm ? `🔍 "${searchTerm}" - ${t.noRecordsMatch}` : t.noRecordsMatch}
              </p>
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm("");
                    setFilterStatus("all");
                  }}
                  style={{
                    marginTop: "12px",
                    padding: "8px 16px",
                    background: "#3b82f6",
                    color: "#fff",
                    border: "none",
                    borderRadius: "8px",
                    cursor: "pointer",
                    fontSize: "13px",
                    fontWeight: "600",
                  }}
                >
                  ✕ {t.clearAll || "Clear"}
                </button>
              )}
            </div>
          )}
        </div>

        {/* "Show more" removed: pagination handled elsewhere */}

      </div>

      {/* History Modal */}
      {showHistoryModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            direction: isRTL ? "rtl" : "ltr",
            // apply blur to everything behind the modal (backdrop)
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
            scrollbarWidth: "none", // للـ Firefox
            msOverflowStyle: "none", // للـ IE و Edge
            WebkitOverflowScrolling: "touch", // للـ iOS
          }}
          onClick={() => setShowHistoryModal(false)}
        >
          <div
            style={{
              background: theme === "dark" ? "#1e293b" : "#fff",
              borderRadius: "20px",
              width: "min(90vw, 1200px)",
              maxHeight: "85vh",
              overflowY: "auto",
              boxShadow:
                theme === "dark"
                  ? "0 20px 60px rgba(0,0,0,0.6)"
                  : "0 20px 60px rgba(0,0,0,0.2)",
              scrollbarWidth: "none", // للـ Firefox
              msOverflowStyle: "none", // للـ IE و Edge
              WebkitOverflowScrolling: "touch", // للـ iOS
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ padding: "24px" }}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "24px",
                  flexDirection: isRTL ? "row" : "row",
                  borderBottom:
                    theme === "dark"
                      ? "1px solid rgba(255,255,255,0.1)"
                      : "1px solid rgba(0,0,0,0.1)",
                  paddingBottom: "16px",
                  scrollbarWidth: "none", // للـ Firefox
                  msOverflowStyle: "none", // للـ IE و Edge
                  WebkitOverflowScrolling: "touch", // للـ iOS
                  backdropFilter: 'blur(8px)',
                  WebkitBackdropFilter: 'blur(8px)'
                }}
              >
                <div>
                  <h2
                    style={{
                      margin: "0 0 16px 0",
                      textAlign: isRTL ? "right" : "left",
                      color: theme === "dark" ? "#fff" : "#0f172a",
                      fontSize: "24px",
                      fontWeight: "700",
                    }}
                  >
                    {t.attendanceHistory} - {selectedEmployee}
                  </h2>
                  {/* Monthly Totals Display */}
                  {employeeHistory.length > 0 && (
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        gap: "12px",
                      }}
                    >
                      <div
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(34, 197, 94, 0.15)"
                              : "rgba(34, 197, 94, 0.1)",
                          borderRadius: "6px",
                          border:
                            theme === "dark"
                              ? "1px solid rgba(34, 197, 94, 0.3)"
                              : "1px solid rgba(34, 197, 94, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "#666",
                            marginBottom: "2px",
                          }}
                        >
                          {t.monthlyOvertimeTotal}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color:
                              theme === "dark"
                                ? "rgba(34, 197, 94, 1)"
                                : "rgb(34, 197, 94)",
                          }}
                        >
                          {employeeHistory.length > 0 &&
                            employeeHistory[0].totalOvertime
                            ? `${Math.round(employeeHistory[0].totalOvertime / 60)}h ${employeeHistory[0].totalOvertime % 60}m`
                            : "0h 0m"}
                        </div>
                      </div>
                      <div
                        style={{
                          padding: "8px 12px",
                          backgroundColor:
                            theme === "dark"
                              ? "rgba(239, 68, 68, 0.15)"
                              : "rgba(239, 68, 68, 0.1)",
                          borderRadius: "6px",
                          border:
                            theme === "dark"
                              ? "1px solid rgba(239, 68, 68, 0.3)"
                              : "1px solid rgba(239, 68, 68, 0.2)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "11px",
                            fontWeight: "600",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.7)"
                                : "#666",
                            marginBottom: "2px",
                          }}
                        >
                          {t.monthlyTardinessTotal}
                        </div>
                        <div
                          style={{
                            fontSize: "16px",
                            fontWeight: "700",
                            color:
                              theme === "dark"
                                ? "rgba(239, 68, 68, 1)"
                                : "rgb(239, 68, 68)",
                          }}
                        >
                          {employeeHistory.length > 0 &&
                            employeeHistory[0].totalUndertime
                            ? `${Math.round(employeeHistory[0].totalUndertime / 60)}h ${employeeHistory[0].totalUndertime % 60}m`
                            : "0h 0m"}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setShowHistoryModal(false)}
                  style={{
                    padding: "8px",
                    borderRadius: "8px",
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    color: theme === "dark" ? "#fff" : "#374151",
                    transition: "background 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background =
                      theme === "dark"
                        ? "rgba(255,255,255,0.1)"
                        : "rgba(0,0,0,0.05)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = "transparent";
                  }}
                >
                  <X size={24} />
                </button>
              </div>

              <div style={{ overflowX: "auto" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "separate",
                    borderSpacing: "0",
                    direction: isRTL ? "rtl" : "ltr",
                    minWidth: "600px",
                  }}
                >
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          background:
                            theme === "dark"
                              ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                              : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                          borderBottom:
                            theme === "dark"
                              ? "2px solid rgba(59, 130, 246, 0.3)"
                              : "2px solid rgba(59, 130, 246, 0.1)",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backdropFilter: "blur(0.625rem)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                              : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t.date}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          background:
                            theme === "dark"
                              ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                              : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                          borderBottom:
                            theme === "dark"
                              ? "2px solid rgba(59, 130, 246, 0.3)"
                              : "2px solid rgba(59, 130, 246, 0.1)",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backdropFilter: "blur(0.625rem)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                              : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t.checkIn}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          background:
                            theme === "dark"
                              ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                              : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                          borderBottom:
                            theme === "dark"
                              ? "2px solid rgba(59, 130, 246, 0.3)"
                              : "2px solid rgba(59, 130, 246, 0.1)",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backdropFilter: "blur(0.625rem)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                              : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t.checkOut}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          background:
                            theme === "dark"
                              ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                              : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                          borderBottom:
                            theme === "dark"
                              ? "2px solid rgba(59, 130, 246, 0.3)"
                              : "2px solid rgba(59, 130, 246, 0.1)",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backdropFilter: "blur(0.625rem)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                              : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t.status}
                      </th>
                      <th
                        style={{
                          padding: "14px 16px",
                          textAlign: "center",
                          fontSize: "12px",
                          fontWeight: "700",
                          letterSpacing: "0.05em",
                          textTransform: "uppercase",
                          background:
                            theme === "dark"
                              ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                              : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                          color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                          borderBottom:
                            theme === "dark"
                              ? "2px solid rgba(59, 130, 246, 0.3)"
                              : "2px solid rgba(59, 130, 246, 0.1)",
                          whiteSpace: "nowrap",
                          position: "sticky",
                          top: 0,
                          zIndex: 10,
                          backdropFilter: "blur(0.625rem)",
                          boxShadow:
                            theme === "dark"
                              ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                              : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                          transition: "all 0.3s ease",
                        }}
                      >
                        {t.hoursWorked}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {employeeHistory.map((rec, idx) => (
                      <tr
                        key={idx}
                        style={{
                          borderBottom:
                            theme === "dark"
                              ? "1px solid rgba(59, 130, 246, 0.1)"
                              : "1px solid rgba(59, 130, 246, 0.05)",
                          transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.background =
                            theme === "dark"
                              ? "rgba(59, 130, 246, 0.08)"
                              : "rgba(59, 130, 246, 0.04)";
                          e.currentTarget.style.boxShadow =
                            theme === "dark"
                              ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
                              : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.background = "transparent";
                          e.currentTarget.style.boxShadow = "none";
                        }}
                      >
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.85)"
                                : "#475569",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(59, 130, 246, 0.1)"
                                : "1px solid rgba(59, 130, 246, 0.05)",
                          }}
                        >
                          {new Date(rec.date).toLocaleDateString(
                            language === "ar" ? "ar-EG" : "en-US",
                            {
                              year: "numeric",
                              month: "long",
                              day: "numeric",
                            }
                          )}
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.85)"
                                : "#475569",
                            direction: "ltr",
                            fontFamily: "'Courier New', monospace",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(59, 130, 246, 0.1)"
                                : "1px solid rgba(59, 130, 246, 0.05)",
                          }}
                        >
                          {rec.checkIn
                            ? new Date(rec.checkIn).toLocaleTimeString(
                              language === "ar" ? "ar-EG" : "en-US",
                              {
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.85)"
                                : "#475569",
                            direction: "ltr",
                            fontFamily: "'Courier New', monospace",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(59, 130, 246, 0.1)"
                                : "1px solid rgba(59, 130, 246, 0.05)",
                          }}
                        >
                          {rec.checkOut
                            ? new Date(rec.checkOut).toLocaleTimeString(
                              language === "ar" ? "ar-EG" : "en-US",
                              {
                                hour12: false,
                                hour: "2-digit",
                                minute: "2-digit",
                              }
                            )
                            : "-"}
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13px",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(59, 130, 246, 0.1)"
                                : "1px solid rgba(59, 130, 246, 0.05)",
                          }}
                        >
                          <span style={getStatusBadgeStyle(rec.status)}>
                            {getStatusText(rec.status)}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: "14px 16px",
                            textAlign: "center",
                            fontSize: "13px",
                            color:
                              theme === "dark"
                                ? "rgba(255,255,255,0.85)"
                                : "#475569",
                            borderBottom:
                              theme === "dark"
                                ? "1px solid rgba(59, 130, 246, 0.1)"
                                : "1px solid rgba(59, 130, 246, 0.05)",
                            fontWeight: "500",
                          }}
                        >
                          {rec.workingHours
                            ? `${rec.workingHours} ${language === "ar" ? "س" : "h"
                            }`
                            : "-"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* CSV IMPORT MODAL */}
      {showImportModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            direction: isRTL ? "rtl" : "ltr",
            // apply blur to everything behind the modal (backdrop)
            backdropFilter: "blur(8px)",
            WebkitBackdropFilter: "blur(8px)",
          }}
          onClick={handleCloseImportModal}
        >
          <div
            style={{
              background: theme === "dark" ? "#1e293b" : "#fff",
              borderRadius: "20px",
              width: "min(95vw, 1400px)",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow:
                theme === "dark"
                  ? "0 20px 60px rgba(0,0,0,0.6)"
                  : "0 20px 60px rgba(0,0,0,0.2)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              style={{
                padding: "24px",
                borderBottom:
                  theme === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexDirection: isRTL ? "row" : "row",
              }}
            >
              <h2
                style={{
                  margin: 0,
                  color: theme === "dark" ? "#fff" : "#0f172a",
                  fontSize: "24px",
                  fontWeight: "700",
                  textAlign: isRTL ? "right" : "left",
                }}
              >
                Import Attendance Records
              </h2>
              <button
                onClick={handleCloseImportModal}
                style={{
                  padding: "8px",
                  borderRadius: "8px",
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  color: theme === "dark" ? "#fff" : "#374151",
                }}
              >
                <X size={24} />
              </button>
            </div>

            {/* Tab Buttons */}
            <div
              style={{
                padding: "16px 24px",
                borderBottom:
                  theme === "dark"
                    ? "1px solid rgba(255,255,255,0.1)"
                    : "1px solid rgba(0,0,0,0.1)",
                display: "flex",
                gap: "12px",
                flexDirection: isRTL ? "row" : "row",
              }}
            >
              <button
                onClick={() => setImportActiveTab("preview")}
                style={{
                  padding: "10px 20px",
                  borderRadius: "8px",
                  border: "none",
                  background:
                    importActiveTab === "preview"
                      ? "#2e7cba"
                      : theme === "dark"
                        ? "rgba(255,255,255,0.08)"
                        : "rgba(0,0,0,0.05)",
                  color:
                    importActiveTab === "preview"
                      ? "#fff"
                      : theme === "dark"
                        ? "#fff"
                        : "#374151",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Preview ({importPreview.length})
              </button>
              {importResult && (
                <button
                  onClick={() => setImportActiveTab("results")}
                  style={{
                    padding: "10px 20px",
                    borderRadius: "8px",
                    border: "none",
                    background:
                      importActiveTab === "results"
                        ? "#2e7cba"
                        : theme === "dark"
                          ? "rgba(255,255,255,0.08)"
                          : "rgba(0,0,0,0.05)",
                    color:
                      importActiveTab === "results"
                        ? "#fff"
                        : theme === "dark"
                          ? "#fff"
                          : "#374151",
                    fontWeight: "600",
                    cursor: "pointer",
                  }}
                >
                  Results
                </button>
              )}
            </div>

            {/* Modal Content */}
            <div style={{ padding: "24px" }}>
              {importActiveTab === "preview" ? (
                <div>
                  {/* CSV Format Template */}
                  {importPreview.length > 0 && (
                    <div
                      style={{
                        padding: "16px",
                        marginBottom: "20px",
                        borderRadius: "8px",
                        border: "2px solid rgba(59, 130, 246, 0.3)",
                        background: "rgba(59, 130, 246, 0.05)",
                      }}
                    >
                      <h4
                        style={{
                          fontSize: "13px",
                          fontWeight: "700",
                          color: "#3b82f6",
                          marginTop: 0,
                          marginBottom: "12px",
                        }}
                      >
                        📋 CSV Format Expected:
                      </h4>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr",
                          gap: "12px",
                          fontSize: "12px",
                        }}
                      >
                        <div>
                          <span
                            style={{
                              fontWeight: "700",
                              color: theme === "dark" ? "#fff" : "#0f172a",
                            }}
                          >
                            Date:
                          </span>{" "}
                          <span
                            style={{
                              color: theme === "dark" ? "#cbd5e1" : "#6b7280",
                              fontFamily: "monospace",
                            }}
                          >
                            dd/mm/yyyy
                          </span>
                        </div>
                        <div>
                          <span
                            style={{
                              fontWeight: "700",
                              color: theme === "dark" ? "#fff" : "#0f172a",
                            }}
                          >
                            Check-In/Out:
                          </span>{" "}
                          <span
                            style={{
                              color: theme === "dark" ? "#cbd5e1" : "#6b7280",
                              fontFamily: "monospace",
                            }}
                          >
                            dd/mm/yyyy hh:mm
                          </span>
                        </div>
                        <div>
                          <span
                            style={{
                              fontWeight: "700",
                              color: theme === "dark" ? "#fff" : "#0f172a",
                            }}
                          >
                            Status:
                          </span>{" "}
                          <span
                            style={{
                              color: theme === "dark" ? "#cbd5e1" : "#6b7280",
                              fontFamily: "monospace",
                            }}
                          >
                            present or absent
                          </span>
                        </div>
                        <div>
                          <span
                            style={{
                              fontWeight: "700",
                              color: theme === "dark" ? "#fff" : "#0f172a",
                            }}
                          >
                            Employee ID:
                          </span>{" "}
                          <span
                            style={{
                              color: theme === "dark" ? "#cbd5e1" : "#6b7280",
                              fontFamily: "monospace",
                            }}
                          >
                            Required
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  <div style={{ overflowX: "auto", marginBottom: "24px" }}>
                    {importPreview.length > 0 ? (
                      <table
                        style={{
                          width: "100%",
                          borderCollapse: "separate",
                          borderSpacing: "0",
                          direction: isRTL ? "rtl" : "ltr",
                          minWidth: "700px",
                        }}
                      >
                        <thead>
                          <tr>
                            <th
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                fontSize: "12px",
                                fontWeight: "700",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                background:
                                  theme === "dark"
                                    ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                                    : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                                color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                                borderBottom:
                                  theme === "dark"
                                    ? "2px solid rgba(59, 130, 246, 0.3)"
                                    : "2px solid rgba(59, 130, 246, 0.1)",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                backdropFilter: "blur(0.625rem)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                                    : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                                transition: "all 0.3s ease",
                                width: "50px",
                              }}
                            >
                              #
                            </th>
                            {importPreview.length > 0 &&
                              Object.keys(importPreview[0]).map((key) => (
                                <th
                                  key={key}
                                  style={{
                                    padding: "14px 16px",
                                    textAlign: "center",
                                    fontSize: "12px",
                                    fontWeight: "700",
                                    letterSpacing: "0.05em",
                                    textTransform: "uppercase",
                                    background:
                                      theme === "dark"
                                        ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                                        : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                                    color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                                    borderBottom:
                                      theme === "dark"
                                        ? "2px solid rgba(59, 130, 246, 0.1)"
                                        : "2px solid rgba(59, 130, 246, 0.05)",
                                    whiteSpace: "nowrap",
                                    position: "sticky",
                                    top: 0,
                                    zIndex: 10,
                                    backdropFilter: "blur(0.625rem)",
                                    boxShadow:
                                      theme === "dark"
                                        ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                                        : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                                    transition: "all 0.3s ease",
                                  }}
                                >
                                  {key}
                                </th>
                              ))}
                            <th
                              style={{
                                padding: "14px 16px",
                                textAlign: "center",
                                fontSize: "12px",
                                fontWeight: "700",
                                letterSpacing: "0.05em",
                                textTransform: "uppercase",
                                background:
                                  theme === "dark"
                                    ? "linear-gradient(135deg, rgba(55, 65, 81, 0.8), rgba(75, 85, 99, 0.8))"
                                    : "linear-gradient(135deg, rgba(248, 250, 252, 0.95), rgba(241, 245, 249, 0.95))",
                                color: theme === "dark" ? "#f1f5f9" : "#1e293b",
                                borderBottom:
                                  theme === "dark"
                                    ? "2px solid rgba(59, 130, 246, 0.3)"
                                    : "2px solid rgba(59, 130, 246, 0.1)",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 10,
                                backdropFilter: "blur(0.625rem)",
                                boxShadow:
                                  theme === "dark"
                                    ? "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.3)"
                                    : "0 0.25rem 0.375rem -0.0625rem rgba(0, 0, 0, 0.1)",
                                transition: "all 0.3s ease",
                                width: "60px",
                              }}
                            >
                              Action
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {importPreview.map((row, rowIdx) => (
                            <tr
                              key={rowIdx}
                              style={{
                                borderBottom:
                                  theme === "dark"
                                    ? "1px solid rgba(59, 130, 246, 0.1)"
                                    : "1px solid rgba(59, 130, 246, 0.05)",
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.background =
                                  theme === "dark"
                                    ? "rgba(59, 130, 246, 0.08)"
                                    : "rgba(59, 130, 246, 0.04)";
                                e.currentTarget.style.boxShadow =
                                  theme === "dark"
                                    ? "inset 0 0 1rem rgba(59, 130, 246, 0.05)"
                                    : "inset 0 0 1rem rgba(59, 130, 246, 0.02)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.background = "transparent";
                                e.currentTarget.style.boxShadow = "none";
                              }}
                            >
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                  fontSize: "13px",
                                  color:
                                    theme === "dark"
                                      ? "rgba(255,255,255,0.7)"
                                      : "#6b7280",
                                  borderBottom:
                                    theme === "dark"
                                      ? "1px solid rgba(59, 130, 246, 0.1)"
                                      : "1px solid rgba(59, 130, 246, 0.05)",
                                  fontWeight: "500",
                                }}
                              >
                                {rowIdx + 1}
                              </td>
                              {Object.keys(row).map((key) => (
                                <td
                                  key={`${rowIdx}-${key}`}
                                  style={{
                                    padding: "10px 12px",
                                    textAlign: "center",
                                    fontSize: "13px",
                                    minWidth: "100px",
                                    borderBottom:
                                      theme === "dark"
                                        ? "1px solid rgba(59, 130, 246, 0.1)"
                                        : "1px solid rgba(59, 130, 246, 0.05)",
                                  }}
                                >
                                  <input
                                    type="text"
                                    value={row[key]}
                                    onChange={(e) =>
                                      handleEditCell(
                                        rowIdx,
                                        key,
                                        e.target.value
                                      )
                                    }
                                    style={{
                                      width: "100%",
                                      padding: "8px",
                                      borderRadius: "6px",
                                      border:
                                        theme === "dark"
                                          ? "1px solid rgba(59, 130, 246, 0.2)"
                                          : "1px solid rgba(59, 130, 246, 0.1)",
                                      background:
                                        theme === "dark"
                                          ? "rgba(59, 130, 246, 0.05)"
                                          : "rgba(59, 130, 246, 0.02)",
                                      color:
                                        theme === "dark" ? "#fff" : "#0f172a",
                                      textAlign: isRTL ? "right" : "left",
                                      direction: isRTL ? "rtl" : "ltr",
                                    }}
                                  />
                                </td>
                              ))}
                              <td
                                style={{
                                  padding: "14px 16px",
                                  textAlign: "center",
                                  borderBottom:
                                    theme === "dark"
                                      ? "1px solid rgba(59, 130, 246, 0.1)"
                                      : "1px solid rgba(59, 130, 246, 0.05)",
                                }}
                              >
                                <button
                                  onClick={() => handleDeleteRow(rowIdx)}
                                  style={{
                                    padding: "6px 12px",
                                    borderRadius: "6px",
                                    border: "none",
                                    background: "linear-gradient(135deg, rgba(239, 68, 68, 0.8), rgba(220, 38, 38, 0.8))",
                                    color: "#fff",
                                    cursor: "pointer",
                                    fontSize: "12px",
                                    fontWeight: "600",
                                    transition: "all 0.3s ease",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.background = "linear-gradient(135deg, rgba(239, 68, 68, 1), rgba(220, 38, 38, 1))";
                                    e.target.style.boxShadow = theme === "dark" ? "0 0 8px rgba(239, 68, 68, 0.4)" : "0 0 8px rgba(239, 68, 68, 0.2)";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.background = "linear-gradient(135deg, rgba(239, 68, 68, 0.8), rgba(220, 38, 38, 0.8))";
                                    e.target.style.boxShadow = "none";
                                  }}
                                >
                                  Delete
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    ) : (
                      <div
                        style={{
                          padding: "40px",
                          textAlign: "center",
                          color:
                            theme === "dark"
                              ? "rgba(255,255,255,0.6)"
                              : "#6b7280",
                        }}
                      >
                        No data
                      </div>
                    )}
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                      flexDirection: isRTL ? "row" : "row",
                    }}
                  >
                    <button
                      onClick={handleCloseImportModal}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "10px",
                        border: "none",
                        background:
                          theme === "dark"
                            ? "rgba(255,255,255,0.1)"
                            : "rgba(0,0,0,0.05)",
                        color: theme === "dark" ? "#fff" : "#374151",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleProcessImport}
                      disabled={isImporting || importPreview.length === 0}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "10px",
                        border: "none",
                        background: "#10b981",
                        color: "#fff",
                        fontWeight: "600",
                        cursor: isImporting ? "not-allowed" : "pointer",
                        opacity:
                          isImporting || importPreview.length === 0 ? 0.6 : 1,
                      }}
                    >
                      {isImporting ? "Importing..." : "Process Import"}
                    </button>
                  </div>
                </div>
              ) : importResult ? (
                <div>
                  {/* Summary Cards */}
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fit, minmax(200px, 1fr))",
                      gap: "16px",
                      marginBottom: "24px",
                    }}
                  >
                    <div
                      style={{
                        padding: "16px",
                        borderRadius: "12px",
                        background: "rgba(16, 185, 129, 0.1)",
                        border: "1px solid rgba(16, 185, 129, 0.3)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#10b981",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        Inserted
                      </div>
                      <div
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          color: "#10b981",
                        }}
                      >
                        {importResult.processed?.length || 0}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: "16px",
                        borderRadius: "12px",
                        background: "rgba(245, 158, 11, 0.1)",
                        border: "1px solid rgba(245, 158, 11, 0.3)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#f59e0b",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        Duplicates
                      </div>
                      <div
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          color: "#f59e0b",
                        }}
                      >
                        {importResult.duplicates?.length || 0}
                      </div>
                    </div>

                    <div
                      style={{
                        padding: "16px",
                        borderRadius: "12px",
                        background: "rgba(239, 68, 68, 0.1)",
                        border: "1px solid rgba(239, 68, 68, 0.3)",
                      }}
                    >
                      <div
                        style={{
                          fontSize: "12px",
                          color: "#ef4444",
                          fontWeight: "600",
                          marginBottom: "8px",
                        }}
                      >
                        Errors
                      </div>
                      <div
                        style={{
                          fontSize: "28px",
                          fontWeight: "700",
                          color: "#ef4444",
                        }}
                      >
                        {importResult.errors?.length || 0}
                      </div>
                    </div>
                  </div>

                  {/* Duplicates Table */}
                  {importResult.duplicates &&
                    importResult.duplicates.length > 0 && (
                      <div style={{ marginBottom: "24px" }}>
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginBottom: "16px",
                            flexWrap: "wrap",
                            gap: "12px",
                          }}
                        >
                          <h3
                            style={{
                              fontSize: "16px",
                              fontWeight: "700",
                              marginBottom: 0,
                              color: theme === "dark" ? "#fff" : "#0f172a",
                            }}
                          >
                            Duplicate Records ({importResult.duplicates.length})
                          </h3>
                          {/* ✅ BULK RESOLUTION BUTTONS */}
                          <div
                            style={{
                              display: "flex",
                              gap: "8px",
                              flexWrap: "wrap",
                            }}
                          >
                            <button
                              onClick={() =>
                                handleResolveDuplicatesBulk("existing")
                              }
                              disabled={isResolvingDuplicates}
                              style={{
                                padding: "8px 14px",
                                borderRadius: "6px",
                                border: "none",
                                background: isResolvingDuplicates
                                  ? "#cbd5e1"
                                  : "#10b981",
                                color: "#fff",
                                fontWeight: "600",
                                cursor: isResolvingDuplicates
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                              }}
                              title="Keep all existing records and discard incoming"
                            >
                              {isResolvingDuplicates
                                ? "⏳ Processing..."
                                : "✓ Use All Existing"}
                            </button>
                            <button
                              onClick={() =>
                                handleResolveDuplicatesBulk("incoming")
                              }
                              disabled={isResolvingDuplicates}
                              style={{
                                padding: "8px 14px",
                                borderRadius: "6px",
                                border: "none",
                                background: isResolvingDuplicates
                                  ? "#cbd5e1"
                                  : "#f59e0b",
                                color: "#fff",
                                fontWeight: "600",
                                cursor: isResolvingDuplicates
                                  ? "not-allowed"
                                  : "pointer",
                                fontSize: "12px",
                                transition: "all 0.2s ease",
                              }}
                              title="Replace all with incoming records"
                            >
                              {isResolvingDuplicates
                                ? "⏳ Processing..."
                                : "⚠ Use All Incoming"}
                            </button>
                          </div>
                        </div>
                        <div style={{ overflowX: "auto" }}>
                          {importResult.duplicates.map((dup, idx) => (
                            <div
                              key={idx}
                              style={{
                                marginBottom: "16px",
                                padding: "16px",
                                borderRadius: "8px",
                                border:
                                  theme === "dark"
                                    ? "1px solid rgba(255,255,255,0.1)"
                                    : "1px solid rgba(0,0,0,0.1)",
                                background:
                                  theme === "dark"
                                    ? "rgba(255,255,255,0.02)"
                                    : "rgba(0,0,0,0.02)",
                              }}
                            >
                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr",
                                  gap: "8px",
                                  marginBottom: "12px",
                                  paddingBottom: "12px",
                                  borderBottom:
                                    theme === "dark"
                                      ? "1px solid rgba(255,255,255,0.1)"
                                      : "1px solid rgba(0,0,0,0.1)",
                                }}
                              >
                                <div
                                  style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr 1fr",
                                    gap: "8px",
                                  }}
                                >
                                  <div>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#94a3b8"
                                            : "#64748b",
                                      }}
                                    >
                                      ROW
                                    </span>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark" ? "#fff" : "#0f172a",
                                      }}
                                    >
                                      {dup.row}
                                    </div>
                                  </div>
                                  <div>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#94a3b8"
                                            : "#64748b",
                                      }}
                                    >
                                      EMPLOYEE ID
                                    </span>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark" ? "#fff" : "#0f172a",
                                      }}
                                    >
                                      {dup.employeeId}
                                    </div>
                                  </div>
                                  <div>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#94a3b8"
                                            : "#64748b",
                                      }}
                                    >
                                      Employee Name
                                    </span>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark" ? "#fff" : "#0f172a",
                                      }}
                                    >
                                      {getEmployeeName(dup.employeeId)}
                                    </div>
                                  </div>

                                  <div>
                                    <span
                                      style={{
                                        fontSize: "10px",
                                        fontWeight: "700",
                                        color:
                                          theme === "dark"
                                            ? "#94a3b8"
                                            : "#64748b",
                                      }}
                                    >
                                      DATE
                                    </span>
                                    <div
                                      style={{
                                        fontSize: "14px",
                                        fontWeight: "600",
                                        color:
                                          theme === "dark" ? "#fff" : "#0f172a",
                                      }}
                                    >
                                      {dup.date}
                                    </div>
                                  </div>
                                </div>
                              </div>

                              <div
                                style={{
                                  display: "grid",
                                  gridTemplateColumns: "1fr 1fr",
                                  gap: "16px",
                                }}
                              >
                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`duplicate-${idx}`}
                                      value="existing"
                                      checked={
                                        duplicateSelections[idx] === "existing"
                                      }
                                      onChange={() =>
                                        handleDuplicateSelection(
                                          idx,
                                          "existing"
                                        )
                                      }
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                      }}
                                    />
                                    <h4
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        color: "#10b981",
                                        marginTop: 0,
                                        marginBottom: 0,
                                      }}
                                    >
                                      ✓ Existing Record
                                    </h4>
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr",
                                      gap: "8px",
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Check-In
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {formatDatetime(dup.existing?.checkIn)}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Check-Out
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {formatDatetime(dup.existing?.checkOut)}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Working Hours
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.existing?.workingHours || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Status
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                        }}
                                      >
                                        {dup.existing?.status || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Overtime
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.existing?.overTime || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Undertime
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.existing?.underTime || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Notes
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                        }}
                                      >
                                        {dup.existing?.notes || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </div>

                                <div>
                                  <div
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      marginBottom: "12px",
                                    }}
                                  >
                                    <input
                                      type="radio"
                                      name={`duplicate-${idx}`}
                                      value="incoming"
                                      checked={
                                        duplicateSelections[idx] === "incoming"
                                      }
                                      onChange={() =>
                                        handleDuplicateSelection(
                                          idx,
                                          "incoming"
                                        )
                                      }
                                      style={{
                                        width: "16px",
                                        height: "16px",
                                        cursor: "pointer",
                                      }}
                                    />
                                    <h4
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: "700",
                                        color: "#f59e0b",
                                        marginTop: 0,
                                        marginBottom: 0,
                                      }}
                                    >
                                      ⚠ Incoming Record
                                    </h4>
                                  </div>
                                  <div
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "1fr",
                                      gap: "8px",
                                    }}
                                  >
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Check-In
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.incoming?.checkIn || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Check-Out
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.incoming?.checkOut || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Working Hours
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.incoming?.workingHours || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Status
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                        }}
                                      >
                                        {dup.incoming?.status || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Overtime
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.incoming?.overTime || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Undertime
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                          fontFamily: "monospace",
                                        }}
                                      >
                                        {dup.incoming?.underTime || "-"}
                                      </div>
                                    </div>
                                    <div>
                                      <span
                                        style={{
                                          fontSize: "11px",
                                          color:
                                            theme === "dark"
                                              ? "#94a3b8"
                                              : "#64748b",
                                        }}
                                      >
                                        Notes
                                      </span>
                                      <div
                                        style={{
                                          fontSize: "13px",
                                          color:
                                            theme === "dark"
                                              ? "#fff"
                                              : "#374151",
                                        }}
                                      >
                                        {dup.incoming?.notes || "-"}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>

                              {/* Action Button */}
                              <div
                                style={{
                                  marginTop: "16px",
                                  paddingTop: "12px",
                                  borderTop:
                                    theme === "dark"
                                      ? "1px solid rgba(255,255,255,0.1)"
                                      : "1px solid rgba(0,0,0,0.1)",
                                  display: "flex",
                                  gap: "8px",
                                  justifyContent: "flex-end",
                                }}
                              >
                                <button
                                  onClick={() =>
                                    handleUpdateDuplicate(
                                      idx,
                                      dup,
                                      duplicateSelections[idx]
                                    )
                                  }
                                  disabled={
                                    !duplicateSelections[idx] ||
                                    isResolvingDuplicates
                                  }
                                  style={{
                                    padding: "8px 16px",
                                    borderRadius: "6px",
                                    border: "none",
                                    background:
                                      duplicateSelections[idx] &&
                                        !isResolvingDuplicates
                                        ? "#3b82f6"
                                        : "#cbd5e1",
                                    color: "#fff",
                                    fontWeight: "600",
                                    cursor:
                                      duplicateSelections[idx] &&
                                        !isResolvingDuplicates
                                        ? "pointer"
                                        : "not-allowed",
                                    fontSize: "12px",
                                    transition: "all 0.2s ease",
                                  }}
                                  title="Resolve this single duplicate record"
                                >
                                  {duplicateSelections[idx]
                                    ? "✓ Confirm This Record"
                                    : "Select a record"}
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Errors Table */}
                  {importResult.errors && importResult.errors.length > 0 && (
                    <div style={{ marginBottom: "24px" }}>
                      <h3
                        style={{
                          fontSize: "16px",
                          fontWeight: "700",
                          marginBottom: "12px",
                          color: theme === "dark" ? "#fff" : "#0f172a",
                        }}
                      >
                        Errors ({importResult.errors.length})
                      </h3>
                      <div style={{ overflowX: "auto" }}>
                        <table
                          style={{
                            width: "100%",
                            borderCollapse: "collapse",
                            fontSize: "12px",
                            direction: isRTL ? "rtl" : "ltr",
                          }}
                        >
                          <thead>
                            <tr>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: isRTL ? "right" : "left",
                                  borderBottom:
                                    theme === "dark"
                                      ? "2px solid rgba(255,255,255,0.1)"
                                      : "2px solid rgba(0,0,0,0.1)",
                                  color: theme === "dark" ? "#fff" : "#0f172a",
                                }}
                              >
                                Row
                              </th>
                              <th
                                style={{
                                  padding: "12px",
                                  textAlign: isRTL ? "right" : "left",
                                  borderBottom:
                                    theme === "dark"
                                      ? "2px solid rgba(255,255,255,0.1)"
                                      : "2px solid rgba(0,0,0,0.1)",
                                  color: theme === "dark" ? "#fff" : "#0f172a",
                                }}
                              >
                                Error Message
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {importResult.errors.map((err, idx) => (
                              <tr
                                key={idx}
                                style={{
                                  borderBottom:
                                    theme === "dark"
                                      ? "1px solid rgba(255,255,255,0.05)"
                                      : "1px solid rgba(0,0,0,0.05)",
                                }}
                              >
                                <td
                                  style={{
                                    padding: "12px",
                                    color:
                                      theme === "dark"
                                        ? "rgba(255,255,255,0.8)"
                                        : "#374151",
                                  }}
                                >
                                  {err.row}
                                </td>
                                <td
                                  style={{
                                    padding: "12px",
                                    color: "#ef4444",
                                    fontWeight: "500",
                                  }}
                                >
                                  {err.error}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  <div
                    style={{
                      display: "flex",
                      gap: "12px",
                      justifyContent: "flex-end",
                      flexDirection: isRTL ? "row" : "row",
                    }}
                  >
                    <button
                      onClick={handleCloseImportModal}
                      style={{
                        padding: "10px 20px",
                        borderRadius: "10px",
                        border: "none",
                        background: "#2e7cba",
                        color: "#fff",
                        fontWeight: "600",
                        cursor: "pointer",
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* CSS for animations */}
      <style jsx>{`
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
          select option {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#1715247e"} !important;
    color: #fff !important;
    padding: 12px;
    
  }
  
  select option:hover {
    background: ${theme === "dark" ? "rgba(2, 38, 97, 0.9)" : "#3a39457e"} !important;
  }

        .animate-spin {
          animation: spin 1s linear infinite;
        }

        .hover-card:hover {
          transform: translateY(-2px);
          box-shadow: ${theme === "dark"
          ? "0 1rem 3rem rgba(0,0,0,0.4)"
          : "0 1rem 3rem rgba(0,0,0,0.15)"} !important;
        }
      `}</style>

      {/* AlertModal Component */}
      <AlertModal
        open={alertModal.open}
        title={alertModal.title}
        message={alertModal.message}
        variant={alertModal.variant}
        showCancel={alertModal.showCancel}
        onConfirm={handleAlertConfirm}
        onClose={closeAlert}
        theme={theme}
        language={language}
      />
    </div>
  );
};

export default Attendance;
